import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { caseApi, formsApi, workflowProtocolsApi } from '../services/api';
import { getBaseUrl } from '../services/baseUrl';
import { CaseAttachmentViewerModal } from './CaseAttachmentViewerModal';
import { TSFADQuoteInactivateModal, TSFADQuoteDeleteModal } from './TSFADAnalysisAuxModals';
import { TSFADQuoteModal } from './TSFADAnalysisQuoteModal';
import WorkflowProtocolEditor, {
  createEmptyWorkflowJson,
  getWorkflowStepChecklist,
  getWorkflowStepParts,
  getWorkflowStepTools,
  normalizeWorkflowJson,
  workflowHasExecutableContent,
  type WorkflowJsonDraft
} from './WorkflowProtocolEditor';
import WorkflowProtocolPreview from './WorkflowProtocolPreview';
import {
  normalizeQuoteTemplateLayout,
  readPathValue,
  type QuoteCatalogPartItem,
  type QuoteLineItemDraft,
  type QuoteTemplateLayout
} from './TSFADAnalysis.shared';
import {
  calculateQuoteTotals,
  normalizeQuoteTotalSettings,
  type QuoteTotalSettings,
  type QuoteTotalsSummary
} from '../utils/quoteTotals';
import TaskDeviceExpertPanel, {
  type TaskDeviceExpertParentOption
} from './TaskDeviceExpertPanel';

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_SURFACE = '#F2EEE7';
const BRAND_PANEL = '#FBF8F2';
const BRAND_CARD = '#FFFDF9';
const BRAND_BORDER = '#DDD4C8';
const BRAND_TEXT_SOFT = 'rgba(18, 63, 67, 0.08)';
const BRAND_TEXT_SOFT_BORDER = 'rgba(18, 63, 67, 0.18)';
const BRAND_SUCCESS_BG = '#E7F2EC';
const BRAND_SUCCESS_SOFT = '#E7F2EC';
const BRAND_SUCCESS_BORDER = '#BDD4C7';
const BRAND_SUCCESS_TEXT = '#25624D';
const BRAND_ACCENT_BG = 'rgba(36, 117, 122, 0.1)';
const BRAND_ACCENT = '#D99A33';
const BRAND_ACCENT_SOFT = '#F6E7C9';
const BRAND_ACCENT_TEXT = '#8A611E';
const BRAND_WARNING_BG = '#FFF3DF';
const BRAND_WARNING_TEXT = '#8A5A00';
const BRAND_WARNING_BORDER = '#F0C888';
const BRAND_DANGER_BG = '#FDEDEC';
const BRAND_DANGER_SOFT = '#FDEDEC';
const BRAND_DANGER_BORDER = '#E7B9B4';
const BRAND_DANGER_TEXT = '#A63B32';

type PreparationSourceMode = 'approved' | 'draft';
type DraftMethod = 'generate' | 'manual';

type WorkPlanSummary = {
  estimatedMinutes: number | null;
  stepCount: number;
  parts: string[];
  tools: string[];
};

const cleanSafetyDeviceExpertText = (value: unknown) => String(value || '').trim();

const buildSafetyWorkflowDeviceExpertOptions = (workflowJson: any): TaskDeviceExpertParentOption[] => {
  const steps = Array.isArray(workflowJson?.steps) ? workflowJson.steps : [];
  return steps
    .map((step: any, index: number): TaskDeviceExpertParentOption => {
      const sequence = Number.isFinite(Number(step?.sequence)) ? Number(step.sequence) : index + 1;
      const stepId = cleanSafetyDeviceExpertText(step?.id || step?.stepKey || `workflow-step-${sequence}`);
      const label =
        cleanSafetyDeviceExpertText(step?.label || step?.title || step?.name) ||
        `Workflow step ${sequence}`;
      const contextOptions = getWorkflowStepChecklist(step)
        .map((item: any, itemIndex: number) => {
          const itemId = cleanSafetyDeviceExpertText(item?.id || item?.key || itemIndex + 1);
          const itemLabel =
            cleanSafetyDeviceExpertText(item?.label || item?.title || item?.name || item?.text) ||
            `Checklist item ${itemIndex + 1}`;
          const itemDetail = cleanSafetyDeviceExpertText(
            item?.spec || item?.description || item?.instruction || item?.notes
          );
          return {
            key: `${stepId}:${itemId}`,
            label: itemLabel,
            detail: itemDetail,
            prompt: [itemLabel, itemDetail].filter(Boolean).join(': ')
          };
        })
        .filter(option => option.label || option.prompt);
      return {
        id: stepId,
        sequence,
        label,
        contextOptions
      };
    })
    .filter((option: TaskDeviceExpertParentOption) => option.label || option.contextOptions.length > 0);
};

type WorkflowQuoteLineItem = {
  id: string;
  lineType: 'part' | 'labor';
  description: string;
  quantity: number;
  unitValue: number;
  isFree: boolean;
  source: string;
  catalogId: string | null;
  sku: string | null;
  partNumber: string | null;
  originalUnitValue: number | null;
  slaCoverageStatus: 'COVERED' | 'DISCOUNTED' | null;
  slaLabel: string | null;
  slaDiscountPercent: number | null;
  slaCoverageReason: string | null;
  slaMatchedListNames: string[];
  lineTotal: number;
};

type WorkflowPreparationQuote = {
  quoteId: string;
  templateId?: string | null;
  title: string;
  description: string;
  status: string;
  lineItems: WorkflowQuoteLineItem[];
  totals: QuoteTotalsSummary;
  totalSettings?: QuoteTotalSettings | null;
  responseActions: string[];
  approvalPlacement: 'after_lines' | 'after_totals' | 'footer_c1' | 'footer_c2' | 'footer_c3';
  approvalFooterSlots: ('footer_c1' | 'footer_c2' | 'footer_c3')[];
  requireSignature: boolean;
  signatureLabel: string;
  requireTechnicianSignature: boolean;
  technicianSignatureLabel: string;
  unmatchedParts: string[];
  pricingUpdatedAt: string | null;
  savedAt: string | null;
  slaCoverage: any;
  laborCoverage: any;
  caseWorkflowSnapshotId?: string | null;
  templateName?: string | null;
  customerDecision?: 'ACCEPT' | 'DENY' | 'ADJUST' | null;
  decisionAt?: string | null;
  decisionBy?: string | null;
  decisionNote?: string | null;
  occurredAt?: string | null;
  actorName?: string | null;
  notes?: string | null;
  customerApprovalName?: string | null;
  customerApprovalSignature?: string | null;
  revisionOfQuoteId?: string | null;
  inactiveReason?: string | null;
  inactiveFromStatus?: string | null;
};

const QUOTE_RESPONSE_ACTIONS = ['accept', 'deny', 'adjust'];

const normalizeQuoteResponseActions = (value: any, fallback?: any): string[] => {
  const source = Array.isArray(value) ? value : Array.isArray(fallback) ? fallback : QUOTE_RESPONSE_ACTIONS;
  const seen = new Set<string>();
  const actions: string[] = [];
  source.forEach((action: any) => {
    const normalised = String(action || '').trim().toLowerCase();
    if (!QUOTE_RESPONSE_ACTIONS.includes(normalised) || seen.has(normalised)) return;
    seen.add(normalised);
    actions.push(normalised);
  });
  return actions;
};

const normalizeQuoteApprovalPlacement = (
  value: any,
  fallback?: any
): 'after_lines' | 'after_totals' | 'footer_c1' | 'footer_c2' | 'footer_c3' => {
  const raw = String(value || fallback || '').trim();
  if (raw === 'footer_c1' || raw === 'footer_c2' || raw === 'footer_c3') return raw;
  if (raw === 'footer') return 'footer_c1';
  return 'footer_c3';
};
const normalizeQuoteApprovalFooterSlots = (
  value: any,
  fallbackPlacement?: any
): ('footer_c1' | 'footer_c2' | 'footer_c3')[] => {
  const slots: ('footer_c1' | 'footer_c2' | 'footer_c3')[] = [];
  const addSlot = (candidate: any) => {
    const raw = String(candidate || '').trim();
    if ((raw === 'footer_c1' || raw === 'footer_c2' || raw === 'footer_c3') && !slots.includes(raw)) {
      slots.push(raw);
    }
  };
  if (Array.isArray(value)) value.forEach(addSlot);
  if (slots.length === 0) addSlot(fallbackPlacement);
  return slots.sort(
    (a, b) => ['footer_c1', 'footer_c2', 'footer_c3'].indexOf(a) - ['footer_c1', 'footer_c2', 'footer_c3'].indexOf(b)
  );
};

type QuoteTemplateInfoBlock = {
  id: string;
  label: string;
  sourceGroup: string;
  included: boolean;
  fields: Array<{
    id: string;
    label: string;
    sourceGroup: string;
    sourceKey: string;
    included: boolean;
  }>;
};

type QuoteHeaderField = {
  id: string;
  label: string;
  value: string;
};

type QuoteHeaderBlock = {
  id: string;
  label: string;
  fields: QuoteHeaderField[];
};

const formatFileSize = (value: any) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const formatDateTime = (value: any) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleString();
};

const formatMoney = (value: any) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const stringArray = (value: any) =>
  Array.isArray(value) ? value.map(item => String(item || '').trim()).filter(Boolean) : [];

const uniqueStrings = (values: string[]) => {
  const seen = new Set<string>();
  return values.filter(value => {
    const key = value.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const parsePositiveInteger = (value: any): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
      ? Number(value.trim())
      : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
};

const parseNonNegativeNumber = (value: any, fallback = 0) => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
      ? Number(value.trim())
      : Number.NaN;
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};

const splitListText = (value: string) =>
  String(value || '')
    .split(/[,;\n]|\s+\|\s+/)
    .map(item => item.trim())
    .filter(Boolean);

const compactCandidate = (value: string) =>
  value
    .replace(/\s+/g, ' ')
    .replace(/^[\s:;.,|-]+|[\s:;.,|-]+$/g, '')
    .trim();

const isRequiredSpecialTool = (value: string) => {
  const text = compactCandidate(value).toLowerCase();
  if (!text) return false;
  if (
    /\b(openquestions?|open questions?|if available|as needed|optional)\b/.test(text) ||
    /\b(ppe|personal protective|laser protection|safety glasses|goggles|gloves?|wrist\s*strap|esd|grounded mat)\b/.test(text) ||
    /\b(pc|laptop|computer|software|integrator|diagnostics?|service pc)\b/.test(text) ||
    /\b(general|standard|basic|normal|ordinary|hand tools?)\b/.test(text) ||
    /\b(allen keys?|hex keys?|screwdrivers?)\b/.test(text)
  ) {
    return false;
  }
  return /\b(calibrat(?:or|ion)|torque|fixture|jig|gauge|meter|analy[sz]er|alignment tool|service tool|adapter tool|special tool)\b/.test(text);
};

const workflowTextValues = (workflowJson: any) => {
  const workflow = normalizeWorkflowJson(workflowJson || {});
  const values = [
    workflow.title,
    workflow.description,
    ...stringArray(workflow.parts),
    ...stringArray(workflow.tools)
  ];
  (Array.isArray(workflow.steps) ? workflow.steps : []).forEach((step: any) => {
    values.push(step?.label || step?.title || '');
    getWorkflowStepParts(step).forEach(item => values.push(item));
    getWorkflowStepTools(step).forEach(item => values.push(item));
    getWorkflowStepChecklist(step).forEach((item: any) => {
      values.push(item?.label || '');
      values.push(item?.spec || '');
    });
  });
  return values.map(value => String(value || '').trim()).filter(Boolean);
};

const normalizeCatalogToken = (value: any) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');

const extractPartNumberTokens = (workflowJson: any) => {
  const tokens = new Set<string>();
  workflowTextValues(workflowJson).forEach(value => {
    const explicitPattern = /\b(?:P\/?N|part(?:\s*number)?)\s*[:#-]?\s*([A-Z0-9][A-Z0-9.-]{2,})\b/gi;
    let match: RegExpExecArray | null;
    while ((match = explicitPattern.exec(value))) {
      const token = normalizeCatalogToken(match[1]);
      if (token && /\d/.test(token)) tokens.add(token);
    }
    (value.match(/\b[A-Z0-9][A-Z0-9.-]{4,}\b/gi) || []).forEach(candidate => {
      const token = normalizeCatalogToken(candidate);
      if (token && /\d/.test(token)) tokens.add(token);
    });
  });
  return tokens;
};

const formatCatalogPart = (item: any) => {
  const partNumber = String(item?.partNumber || item?.sku || '').trim();
  const description = String(item?.description || item?.name || '').trim();
  return [partNumber, description].filter(Boolean).join(' | ');
};

const formatCatalogKit = (kit: any) => {
  const kitNumber = String(kit?.kitNumber || kit?.sku || '').trim();
  const name = String(kit?.name || kit?.description || 'Kit').trim();
  return [kitNumber, name].filter(Boolean).join(' | ');
};

const formatPreparedCatalogItem = (item: any) => {
  const partNumber = String(item?.partNumber || item?.kitNumber || item?.sku || '').trim();
  const description = String(item?.description || item?.name || '').trim();
  return [partNumber, description].filter(Boolean).join(' | ');
};

const extractCatalogParts = (workflowJson: any, partCatalog: any[], kitCatalog: any[]) => {
  const workflow = normalizeWorkflowJson(workflowJson || {});
  const preparation = (workflow as any)?.preparation && typeof (workflow as any).preparation === 'object'
    ? (workflow as any).preparation
    : {};
  const structuredParts = Array.isArray(preparation.requiredParts)
    ? preparation.requiredParts
    : Array.isArray(preparation.parts)
      ? preparation.parts
      : [];
  const catalogStructuredParts = structuredParts
    .filter((item: any) => String(item?.source || '').toUpperCase() === 'CATALOG' || item?.catalogId)
    .map(formatPreparedCatalogItem)
    .filter(Boolean);
  if (catalogStructuredParts.length > 0) {
    return uniqueStrings(catalogStructuredParts.map(compactCandidate)).slice(0, 16);
  }

  const tokens = extractPartNumberTokens(workflowJson);
  if (tokens.size === 0) return [];

  const kitMatches = (Array.isArray(kitCatalog) ? kitCatalog : []).filter(kit => {
    const kitTokens = [kit?.kitNumber, kit?.sku].map(normalizeCatalogToken).filter(Boolean);
    return kitTokens.some(token => tokens.has(token));
  });

  const kitComponentKeys = new Set<string>();
  kitMatches.forEach(kit => {
    (Array.isArray(kit?.components) ? kit.components : []).forEach((component: any) => {
      const key = normalizeCatalogToken(component?.partNumber);
      if (key) kitComponentKeys.add(key);
    });
  });

  const catalogByKey = new Map<string, any>();
  (Array.isArray(partCatalog) ? partCatalog : []).forEach(item => {
    [item?.partNumber, item?.sku].map(normalizeCatalogToken).filter(Boolean).forEach(key => {
      if (!catalogByKey.has(key)) catalogByKey.set(key, item);
    });
  });

  const matchedParts: string[] = [];
  tokens.forEach(token => {
    if (kitComponentKeys.has(token)) return;
    const item = catalogByKey.get(token);
    if (item) matchedParts.push(formatCatalogPart(item));
  });

  return uniqueStrings([
    ...kitMatches.map(formatCatalogKit),
    ...matchedParts
  ].map(compactCandidate)).slice(0, 16);
};

const extractToolCandidates = (workflowJson: any) => {
  const workflow = normalizeWorkflowJson(workflowJson || {});
  const explicitTools = [
    ...stringArray(workflow.tools),
    ...(Array.isArray(workflow.steps) ? workflow.steps.flatMap((step: any) => getWorkflowStepTools(step)) : [])
  ];
  const fromToolLabels: string[] = [];
  workflowTextValues(workflow).forEach(value => {
    const toolPattern = /\b(?:special\s+tools?|required\s+tools?|tools?)\s*:\s*([^.;\n]+)/gi;
    let match: RegExpExecArray | null;
    while ((match = toolPattern.exec(value))) {
      fromToolLabels.push(...splitListText(match[1]));
    }
  });
  return uniqueStrings([...explicitTools, ...fromToolLabels].map(compactCandidate).filter(isRequiredSpecialTool)).slice(0, 12);
};

const buildWorkPlanSummary = (workflowJson: any, catalogs: { partCatalog?: any[]; kitCatalog?: any[] } = {}): WorkPlanSummary => {
  const workflow = normalizeWorkflowJson(workflowJson || {});
  const steps = Array.isArray(workflow.steps) ? workflow.steps : [];
  const preparation = (workflow as any)?.preparation && typeof (workflow as any).preparation === 'object'
    ? (workflow as any).preparation
    : {};
  const specialTools = Array.isArray(preparation.specialTools)
    ? preparation.specialTools.map(compactCandidate).filter(isRequiredSpecialTool)
    : [];
  return {
    estimatedMinutes: parsePositiveInteger(workflow.estimatedDurationMinutes || workflow.estimatedTimeMinutes),
    stepCount: steps.length,
    parts: extractCatalogParts(workflow, catalogs.partCatalog || [], catalogs.kitCatalog || []),
    tools: specialTools.length > 0 ? uniqueStrings(specialTools).slice(0, 12) : extractToolCandidates(workflow)
  };
};

const createQuoteLineId = (prefix: string) => `workflow-quote-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createWorkflowQuoteId = () => `safety-upgrade-quote-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const computeQuoteTotals = (lineItems: WorkflowQuoteLineItem[], totalSettings?: unknown): QuoteTotalsSummary =>
  calculateQuoteTotals(lineItems, totalSettings);

const buildSafetyQuoteHeaderBlocks = (
  templateLayout: QuoteTemplateLayout | null,
  quote: WorkflowPreparationQuote | null,
  caseRecord: any,
  taskRecord: any
): QuoteHeaderBlock[] => {
  if (!templateLayout || !quote) return [];

  const primaryContact =
    caseRecord?.customer?.contacts?.find?.((entry: any) => entry?.isPrimary) ||
    caseRecord?.customer?.contacts?.[0] ||
    null;
  const context = {
    customer: {
      companyName: caseRecord?.customer?.companyName || caseRecord?.customer?.company || '',
      contact: {
        fullName: primaryContact?.fullName || '',
        email: primaryContact?.email || '',
        phone: primaryContact?.phone || ''
      }
    },
    device: {
      modelNumber: caseRecord?.device?.modelNumber || '',
      serialNumber: caseRecord?.device?.serialNumber || '',
      manufacturer: caseRecord?.device?.manufacturer || ''
    },
    case: {
      caseNumber: caseRecord?.caseCode || caseRecord?.caseNumber || caseRecord?.id || '',
      priority: caseRecord?.priority || '',
      status: caseRecord?.status || ''
    },
    task: {
      taskType: taskRecord?.type?.label || taskRecord?.type || '',
      assignedUserName:
        taskRecord?.assignees?.[0]?.fullName || taskRecord?.assignees?.[0]?.username || ''
    },
    quote: {
      title: quote.title,
      total: formatMoney(quote.totals?.grandTotal || 0)
    }
  } as Record<string, any>;

  const byId = templateLayout.infoBlocks.reduce<Record<string, QuoteTemplateInfoBlock>>((acc, block) => {
    acc[block.id] = block;
    return acc;
  }, {});

  return Object.keys(templateLayout.headerAssignments || {})
    .filter(slotId => {
      if (templateLayout.headerRows === 1) {
        return slotId.startsWith('h1');
      }
      return true;
    })
    .map(slotId => templateLayout.headerAssignments[slotId])
    .filter(Boolean)
    .map(slotId => String(slotId))
    .map(blockId => byId[blockId])
    .filter((block): block is QuoteTemplateInfoBlock => Boolean(block && block.included))
    .map(block => ({
      id: block.id,
      label: block.label,
      fields: block.fields
        .filter(field => field.included)
        .map(field => {
          const sourceGroup = String(field.sourceGroup || '').trim().toLowerCase();
          const sourceObj = context[sourceGroup] || {};
          const value = readPathValue(sourceObj, field.sourceKey);
          return {
            id: field.id,
            label: field.label,
            value: String(value || '').trim() || 'Not provided'
          };
        })
    }));
};

const normalizeQuoteLineItem = (value: any, index: number): WorkflowQuoteLineItem => {
  const lineType = String(value?.lineType || 'part').toLowerCase() === 'labor' ? 'labor' : 'part';
  const quantity = Number(parseNonNegativeNumber(value?.quantity, 0).toFixed(2));
  const unitValue = Number(parseNonNegativeNumber(value?.unitValue, 0).toFixed(2));
  const isFree = Boolean(value?.isFree);
  const lineTotal = isFree ? 0 : Number((quantity * unitValue).toFixed(2));
  const rawCoverageStatus = String(value?.slaCoverageStatus || '').trim().toUpperCase();
  const slaCoverageStatus =
    rawCoverageStatus === 'COVERED' || rawCoverageStatus === 'DISCOUNTED' ? rawCoverageStatus : null;
  return {
    id: String(value?.id || createQuoteLineId(String(index + 1))).trim(),
    lineType,
    description: String(value?.description || '').trim() || (lineType === 'labor' ? 'Service labor' : 'Part'),
    quantity,
    unitValue,
    isFree,
    source: String(value?.source || 'manual').trim() || 'manual',
    catalogId: String(value?.catalogId || '').trim() || null,
    sku: String(value?.sku || '').trim() || null,
    partNumber: String(value?.partNumber || '').trim() || null,
    originalUnitValue:
      Number.isFinite(Number(value?.originalUnitValue)) && Number(value.originalUnitValue) >= 0
        ? Number(Number(value.originalUnitValue).toFixed(2))
        : null,
    slaCoverageStatus,
    slaLabel: String(value?.slaLabel || '').trim() || null,
    slaDiscountPercent:
      Number.isFinite(Number(value?.slaDiscountPercent)) && Number(value.slaDiscountPercent) >= 0
        ? Number(Number(value.slaDiscountPercent).toFixed(2))
        : null,
    slaCoverageReason: String(value?.slaCoverageReason || '').trim() || null,
    slaMatchedListNames: Array.isArray(value?.slaMatchedListNames)
      ? value.slaMatchedListNames.map((item: any) => String(item || '').trim()).filter(Boolean)
      : [],
    lineTotal
  };
};

const normalizePreparationQuote = (value: any): WorkflowPreparationQuote | null => {
  if (!value || typeof value !== 'object') return null;
  const lineItems = Array.isArray(value.lineItems)
    ? value.lineItems.map((line: any, index: number) => normalizeQuoteLineItem(line, index))
    : [];
  const totalSettings = value.totalSettings || value.totals?.totalSettings
    ? normalizeQuoteTotalSettings(value.totalSettings || value.totals?.totalSettings)
    : null;
  const approvalSettings =
    value.approvalSettings && typeof value.approvalSettings === 'object'
      ? value.approvalSettings
      : value.totals?.approvalSettings && typeof value.totals.approvalSettings === 'object'
        ? value.totals.approvalSettings
        : {};
  const requireSignature =
    typeof value.requireSignature === 'boolean'
      ? value.requireSignature
      : typeof approvalSettings.requireSignature === 'boolean'
        ? approvalSettings.requireSignature
        : false;
  const requireTechnicianSignature =
    typeof value.requireTechnicianSignature === 'boolean'
      ? value.requireTechnicianSignature
      : typeof approvalSettings.requireTechnicianSignature === 'boolean'
        ? approvalSettings.requireTechnicianSignature
        : false;
  const approvalFooterSlots = normalizeQuoteApprovalFooterSlots(
    value.approvalFooterSlots,
    approvalSettings.approvalFooterSlots || value.approvalPlacement || approvalSettings.approvalPlacement
  );
  return {
    quoteId: String(value.quoteId || '').trim() || `safety-upgrade-quote-${Date.now()}`,
    title: String(value.title || '').trim() || 'Safety / upgrade quote',
    description: String(value.description || '').trim(),
    status: String(value.status || 'DRAFT').trim() || 'DRAFT',
    lineItems,
    totals: computeQuoteTotals(lineItems, totalSettings),
    totalSettings,
    responseActions: normalizeQuoteResponseActions(value.responseActions, approvalSettings.responseActions),
    approvalPlacement: approvalFooterSlots[0] || normalizeQuoteApprovalPlacement(value.approvalPlacement, approvalSettings.approvalPlacement),
    approvalFooterSlots,
    requireSignature,
    signatureLabel:
      String(value.signatureLabel || approvalSettings.signatureLabel || '').trim() ||
      'Customer approval signature',
    requireTechnicianSignature,
    technicianSignatureLabel:
      String(value.technicianSignatureLabel || approvalSettings.technicianSignatureLabel || '').trim() ||
      'Technician signature',
    unmatchedParts: Array.isArray(value.unmatchedParts)
      ? value.unmatchedParts.map((item: any) => String(item || '').trim()).filter(Boolean)
      : [],
    pricingUpdatedAt: String(value.pricingUpdatedAt || '').trim() || null,
    savedAt: String(value.savedAt || '').trim() || null,
    slaCoverage: value.slaCoverage && typeof value.slaCoverage === 'object' ? value.slaCoverage : null,
    laborCoverage: value.laborCoverage && typeof value.laborCoverage === 'object' ? value.laborCoverage : null,
    caseWorkflowSnapshotId: String(value.caseWorkflowSnapshotId || '').trim() || null,
    templateName: String(value.templateName || '').trim() || null,
    templateId: String(value.templateId || '').trim() || null,
    customerDecision:
      ['ACCEPT', 'DENY', 'ADJUST'].includes(String(value.customerDecision || '').trim().toUpperCase())
        ? (String(value.customerDecision || '').trim().toUpperCase() as 'ACCEPT' | 'DENY' | 'ADJUST')
        : null,
    decisionNote: String(value.decisionNote || '').trim() || null,
    decisionAt: String(value.decisionAt || '').trim() || null,
    decisionBy: String(value.decisionBy || '').trim() || null,
    occurredAt: String(value.occurredAt || '').trim() || null,
    actorName: String(value.actorName || '').trim() || null,
    notes: String(value.notes || '').trim() || null,
    customerApprovalName: String(value.customerApprovalName || '').trim() || null,
    customerApprovalSignature: String(value.customerApprovalSignature || '').trim() || null,
    revisionOfQuoteId: String(value.revisionOfQuoteId || '').trim() || null,
    inactiveReason: String(value.inactiveReason || '').trim() || null,
    inactiveFromStatus: String(value.inactiveFromStatus || '').trim() || null
  };
};

const normaliseWorkflowQuoteStatus = (value: any) => {
  const status = String(value || '').trim().toUpperCase();
  if (
    status === 'DRAFT' ||
    status === 'SENT' ||
    status === 'INACTIVE' ||
    status === 'ACCEPTED' ||
    status === 'DENIED' ||
    status === 'ADJUST_REQUESTED'
  ) {
    return status;
  }
  return '';
};

const getWorkflowQuoteStatusLabel = (quote: WorkflowPreparationQuote) => {
  const status = String(quote?.status || '').trim().toUpperCase();
  if (status === 'DRAFT') return 'Draft';
  if (status === 'SENT' || status === 'PENDING') return 'Waiting response';
  if (status === 'INACTIVE') return 'Inactive';
  if (status === 'ACCEPTED') return 'Approved';
  if (status === 'DENIED') return 'Denied';
  if (status === 'ADJUST_REQUESTED') return 'Adjust requested';
  return 'Saved';
};

const getWorkflowQuoteDecisionLabel = (quote: WorkflowPreparationQuote) => {
  const decision = String(quote?.customerDecision || '').trim().toUpperCase();
  if (decision === 'ACCEPT') return 'Accepted';
  if (decision === 'DENY') return 'Denied';
  if (decision === 'ADJUST') return 'Adjust requested';
  return null;
};

const getWorkflowQuoteDecisionBadgeStyle = (quote: WorkflowPreparationQuote): React.CSSProperties => {
  const base: React.CSSProperties = {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderRadius: '999px',
    padding: '0.1rem 0.45rem',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    backgroundColor: BRAND_TEXT_SOFT,
    color: BRAND_TEXT
  };
  const decision = String(quote?.customerDecision || '').trim().toUpperCase();
  if (decision === 'ACCEPT') {
    return {
      ...base,
      backgroundColor: BRAND_SUCCESS_SOFT,
      border: `1px solid ${BRAND_SUCCESS_BORDER}`,
      color: BRAND_SUCCESS_TEXT
    };
  }
  if (decision === 'DENY') {
    return {
      ...base,
      backgroundColor: BRAND_DANGER_SOFT,
      border: `1px solid ${BRAND_DANGER_BORDER}`,
      color: BRAND_DANGER_TEXT
    };
  }
  if (decision === 'ADJUST') {
    return {
      ...base,
      backgroundColor: BRAND_ACCENT_SOFT,
      border: `1px solid ${BRAND_ACCENT}`,
      color: BRAND_ACCENT_TEXT
    };
  }
  return base;
};

const getWorkflowQuoteStatusBadgeStyle = (quote: WorkflowPreparationQuote): React.CSSProperties => {
  const base: React.CSSProperties = {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderRadius: '999px',
    padding: '0.1rem 0.45rem',
    backgroundColor: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
  };
  const status = String(quote?.status || '').trim().toUpperCase();
  if (status === 'ACCEPTED') {
    return {
      ...base,
      backgroundColor: BRAND_SUCCESS_SOFT,
      border: `1px solid ${BRAND_SUCCESS_BORDER}`,
      color: BRAND_SUCCESS_TEXT
    };
  }
  if (status === 'DENIED') {
    return {
      ...base,
      backgroundColor: BRAND_DANGER_SOFT,
      border: `1px solid ${BRAND_DANGER_BORDER}`,
      color: BRAND_DANGER_TEXT
    };
  }
  if (status === 'ADJUST_REQUESTED') {
    return {
      ...base,
      backgroundColor: BRAND_ACCENT_SOFT,
      border: `1px solid ${BRAND_ACCENT}`,
      color: BRAND_ACCENT_TEXT
    };
  }
  return base;
};

const getWorkflowSourceDocument = (workflow: any) => {
  const metadata = workflow?.metadata && typeof workflow.metadata === 'object' ? workflow.metadata : {};
  const sourceDocument =
    metadata.sourceDocument ||
    (workflow?.workflowJson?.source && typeof workflow.workflowJson.source === 'object'
      ? workflow.workflowJson.source
      : null);
  return sourceDocument && typeof sourceDocument === 'object' ? sourceDocument : null;
};

const resolveFileUrl = (baseUrl: string, fileUrl: string | null | undefined) => {
  const raw = String(fileUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(baseUrl || '').replace(/\/$/, '');
  if (!base) return raw;
  return `${base}${raw.startsWith('/') ? raw : `/${raw}`}`;
};

const SafetyUpgradeTask: React.FC = () => {
  const { caseId = '' } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [action, setAction] = React.useState('');
  const [preparationSourceMode, setPreparationSourceMode] = React.useState<PreparationSourceMode>('approved');
  const [replaceWorkflowSource, setReplaceWorkflowSource] = React.useState(false);
  const [draftMethod, setDraftMethod] = React.useState<DraftMethod>('generate');
  const [draftEditing, setDraftEditing] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState('');
  const [selectedMediaId, setSelectedMediaId] = React.useState('');
  const [draftTitle, setDraftTitle] = React.useState('');
  const [draftDescription, setDraftDescription] = React.useState('');
  const [draftWorkflowJson, setDraftWorkflowJson] = React.useState<WorkflowJsonDraft>(() =>
    createEmptyWorkflowJson({ category: 'SAFETY_UPGRADE' })
  );
  const [generatedDraft, setGeneratedDraft] = React.useState<any | null>(null);
  const [attachmentViewer, setAttachmentViewer] = React.useState<any | null>(null);
  const [workflowViewer, setWorkflowViewer] = React.useState<any | null>(null);
  const [baseUrl, setBaseUrl] = React.useState('');
  const [quoteDraft, setQuoteDraft] = React.useState<WorkflowPreparationQuote | null>(null);
  const [quoteModalOpen, setQuoteModalOpen] = React.useState(false);
  const [quoteLoading, setQuoteLoading] = React.useState(false);
  const [quoteSaving, setQuoteSaving] = React.useState(false);
  const [quoteSending, setQuoteSending] = React.useState(false);
  const [quoteError, setQuoteError] = React.useState('');
  const [quoteRevisionSourceId, setQuoteRevisionSourceId] = React.useState<string | null>(null);
  const [quoteTemplateLayout, setQuoteTemplateLayout] = React.useState<QuoteTemplateLayout | null>(null);
  const [quoteTemplateName, setQuoteTemplateName] = React.useState<string | null>(null);
  const [selectedQuoteLineIds, setSelectedQuoteLineIds] = React.useState<string[]>([]);
  const [quoteInactivateTarget, setQuoteInactivateTarget] =
    React.useState<WorkflowPreparationQuote | null>(null);
  const [quoteInactivationReason, setQuoteInactivationReason] = React.useState('');
  const [quoteInactivationError, setQuoteInactivationError] = React.useState<string | null>(null);
  const [quoteStateChangeId, setQuoteStateChangeId] = React.useState<string | null>(null);
  const [quoteDeleteTarget, setQuoteDeleteTarget] = React.useState<WorkflowPreparationQuote | null>(null);
  const [quoteDeleteError, setQuoteDeleteError] = React.useState<string | null>(null);
  const [quoteDeleteId, setQuoteDeleteId] = React.useState<string | null>(null);

  const snapshot = workspace?.snapshot || null;
  const media = React.useMemo(() => (Array.isArray(workspace?.media) ? workspace.media : []), [workspace?.media]);
  const workflows = React.useMemo(
    () => (Array.isArray(workspace?.workflows) ? workspace.workflows : []),
    [workspace?.workflows]
  );
  const partCatalog = React.useMemo(
    () => (Array.isArray(workspace?.partCatalog) ? workspace.partCatalog : []),
    [workspace?.partCatalog]
  );
  const kitCatalog = React.useMemo(
    () => (Array.isArray(workspace?.kitCatalog) ? workspace.kitCatalog : []),
    [workspace?.kitCatalog]
  );
  const quotePartCatalog = React.useMemo<QuoteCatalogPartItem[]>(
    () =>
      partCatalog.map((item: any, index: number) => ({
        id: String(item?.id || item?.sku || item?.partNumber || `part-${index}`),
        sku: String(item?.sku || '').trim() || null,
        partNumber: String(item?.partNumber || item?.sku || '').trim() || null,
        description: String(item?.description || item?.name || '').trim(),
        unitValue: parseNonNegativeNumber(
          item?.unitValue ?? item?.price ?? item?.customerPrice ?? item?.listPrice,
          0
        ),
        lineType: 'part'
      })),
    [partCatalog]
  );
  const filteredWorkflows = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return workflows;
    return workflows.filter((workflow: any) =>
      [workflow?.title, workflow?.description, workflow?.deviceModelLabel, workflow?.sourceLabel]
        .map(value => String(value || '').toLowerCase())
        .some(value => value.includes(query))
    );
  }, [search, workflows]);
  const workflowJson = React.useMemo(() => snapshot?.workflowJson || {}, [snapshot?.workflowJson]);
  const savedPreparationQuote = React.useMemo(
    () => normalizePreparationQuote(snapshot?.resultsJson?.preparationQuote),
    [snapshot?.resultsJson?.preparationQuote]
  );
  const savedWorkflowQuotes = React.useMemo(
    () =>
      (Array.isArray(workspace?.quotes) ? workspace.quotes : [])
        .map((quote: any) => normalizePreparationQuote(quote))
        .filter((quote: WorkflowPreparationQuote | null): quote is WorkflowPreparationQuote => quote !== null),
    [workspace?.quotes]
  );
  const latestSavedWorkflowQuote =
    savedWorkflowQuotes.length > 0 ? savedWorkflowQuotes[savedWorkflowQuotes.length - 1] : null;
  const displayPreparationQuote = React.useMemo(() => {
    const matchingRelationalQuote =
      savedPreparationQuote?.quoteId
        ? savedWorkflowQuotes.find(
            (quote: WorkflowPreparationQuote) =>
              String(quote?.quoteId || '').trim() === String(savedPreparationQuote.quoteId || '').trim()
          ) || null
        : null;
    const selectedWorkflowQuote = matchingRelationalQuote || latestSavedWorkflowQuote;
    if (!savedPreparationQuote && !selectedWorkflowQuote) return null;
    if (!savedPreparationQuote) return selectedWorkflowQuote;
    if (!selectedWorkflowQuote) return savedPreparationQuote;
    return normalizePreparationQuote({
      ...savedPreparationQuote,
      ...selectedWorkflowQuote,
      lineItems:
        Array.isArray(selectedWorkflowQuote.lineItems) && selectedWorkflowQuote.lineItems.length > 0
          ? selectedWorkflowQuote.lineItems
          : savedPreparationQuote.lineItems
    });
  }, [latestSavedWorkflowQuote, savedPreparationQuote, savedWorkflowQuotes]);
  const caseRecord = workspace?.case || {};
  const taskRecord = workspace?.task || {};
  const quoteHeaderBlocks = React.useMemo(
    () => buildSafetyQuoteHeaderBlocks(quoteTemplateLayout, quoteDraft, caseRecord, taskRecord),
    [quoteTemplateLayout, quoteDraft, caseRecord, taskRecord]
  );
  const canPrepare = workspace?.canPrepare !== false;

  const loadWorkspace = React.useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError('');
    try {
      const payload = await caseApi.getSafetyWorkflowWorkspace(caseId);
      setWorkspace(payload || null);
      const nextMedia = Array.isArray(payload?.media) ? payload.media : [];
      const nextWorkflows = Array.isArray(payload?.workflows) ? payload.workflows : [];
      setSelectedMediaId(current =>
        nextMedia.some((item: any) => String(item?.id) === current) ? current : String(nextMedia[0]?.id || '')
      );
      setSelectedWorkflowId(current => {
        return nextWorkflows.some((item: any) => String(item?.id) === current) ? current : '';
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to load safety / upgrade task.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  React.useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  React.useEffect(() => {
    getBaseUrl()
      .then(value => setBaseUrl(value || ''))
      .catch(() => setBaseUrl(''));
  }, []);

  React.useEffect(() => {
    setQuoteDraft(displayPreparationQuote);
  }, [displayPreparationQuote]);

  const loadApprovedWorkflow = async () => {
    if (!caseId || !selectedWorkflowId || !canPrepare) return;
    setAction('select');
    setError('');
    try {
      const payload = await caseApi.selectSafetyWorkflow(caseId, selectedWorkflowId);
      setWorkspace((current: any) => ({ ...(current || {}), snapshot: payload?.snapshot || null }));
      setReplaceWorkflowSource(false);
      setGeneratedDraft(null);
      setDraftWorkflowJson(createEmptyWorkflowJson({ category: 'SAFETY_UPGRADE' }));
      await loadWorkspace();
    } catch (err: any) {
      setError(err?.message || 'Failed to load approved workflow.');
    } finally {
      setAction('');
    }
  };

  const createWorkflowDraft = async () => {
    if (!caseId || !selectedMediaId || !canPrepare) return;
    setAction('generate');
    setError('');
    try {
      const payload = await caseApi.generateSafetyWorkflow(caseId, {
        caseMediaId: selectedMediaId,
        title: draftTitle,
        description: draftDescription,
        category: 'SAFETY_UPGRADE'
      });
      const draft = payload?.draft || {};
      setGeneratedDraft(draft);
      setPreparationSourceMode('draft');
      setDraftMethod('generate');
      setDraftEditing(false);
      setDraftTitle(String(draft.title || draftTitle || '').trim());
      setDraftDescription(String(draft.description || draftDescription || '').trim());
      setDraftWorkflowJson(
        normalizeWorkflowJson(draft.workflowJson, {
          title: String(draft.title || draftTitle || '').trim(),
          description: String(draft.description || draftDescription || '').trim(),
          category: 'SAFETY_UPGRADE'
        })
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to create workflow from the selected notice.');
    } finally {
      setAction('');
    }
  };

  const saveWorkflowDraft = async (
    sourceType: 'CASE_GENERATED_WORKFLOW' | 'CASE_MANUAL_WORKFLOW',
    actionKey = 'save-case'
  ) => {
    if (!caseId || !canPrepare) return null;
    const title = draftTitle.trim();
    if (!title) {
      setError('Workflow title is required.');
      return null;
    }
    const workflowJson = normalizeWorkflowJson(draftWorkflowJson, {
      title,
      description: draftDescription.trim(),
      category: 'SAFETY_UPGRADE'
    });
    if (!workflowHasExecutableContent(workflowJson)) {
      setError('Add at least one workflow step before saving.');
      return null;
    }
    if (!workflowJson.source || typeof workflowJson.source !== 'object') {
      workflowJson.source = { type: sourceType, label: 'Manual workflow' };
    }
    setAction(actionKey);
    setError('');
    try {
      const payload = await caseApi.saveSafetyWorkflowSnapshot(caseId, {
        title,
        description: draftDescription.trim(),
        category: 'SAFETY_UPGRADE',
        sourceType,
        sourceId: generatedDraft?.sourceId,
        sourceLabel: generatedDraft?.sourceLabel,
        workflowJson,
        metadata: generatedDraft?.metadata || {}
      });
      setGeneratedDraft(null);
      setDraftWorkflowJson(createEmptyWorkflowJson({ category: 'SAFETY_UPGRADE' }));
      setReplaceWorkflowSource(false);
      setWorkspace((current: any) => ({ ...(current || {}), snapshot: payload?.snapshot || null }));
      await loadWorkspace();
      return payload?.snapshot || null;
    } catch (err: any) {
      setError(err?.message || 'Failed to save workflow for this task.');
      return null;
    } finally {
      setAction('');
    }
  };

  const loadSnapshotForEditing = () => {
    if (!snapshot) return;
    setGeneratedDraft(null);
    setReplaceWorkflowSource(false);
    setPreparationSourceMode('draft');
    setDraftMethod('manual');
    setDraftEditing(true);
    setDraftTitle(String(snapshot.title || snapshot.workflowJson?.title || '').trim());
    setDraftDescription(String(snapshot.description || snapshot.workflowJson?.description || '').trim());
    setDraftWorkflowJson(
      normalizeWorkflowJson(snapshot.workflowJson, {
        title: String(snapshot.title || snapshot.workflowJson?.title || '').trim(),
        description: String(snapshot.description || snapshot.workflowJson?.description || '').trim(),
        category: 'SAFETY_UPGRADE'
      })
    );
  };

  const caseLabel = caseRecord.caseCode || caseRecord.id || caseId || 'Case';
  const customerLabel = caseRecord.customer?.company || 'Customer';
  const deviceParts = [
    caseRecord.device?.modelNumber || 'Device',
    caseRecord.device?.assetNumber ? `Asset ${caseRecord.device.assetNumber}` : '',
    caseRecord.device?.serialNumber ? `Serial ${caseRecord.device.serialNumber}` : ''
  ].filter(Boolean);
  const deviceLabel = deviceParts.join(' | ');
  const taskLabel = taskRecord.type?.label || 'Safety / Upgrades';
  const problemLabel = caseRecord.title || 'Safety / upgrade task';
  const selectedWorkflow = workflows.find((workflow: any) => String(workflow?.id) === selectedWorkflowId) || null;
  const approvedWorkflowOptions = React.useMemo(() => {
    const selectedId = String(selectedWorkflow?.id || '').trim();
    const options = Array.isArray(filteredWorkflows) ? [...filteredWorkflows] : [];
    if (selectedWorkflow && !options.some((workflow: any) => String(workflow?.id) === selectedId)) {
      options.unshift(selectedWorkflow);
    }
    return options;
  }, [filteredWorkflows, selectedWorkflow]);
  const selectedMedia = media.find((item: any) => String(item?.id) === selectedMediaId) || null;
  const selectedWorkflowArtifact = React.useMemo(
    () => selectedWorkflow?.workflowJson || {},
    [selectedWorkflow?.workflowJson]
  );
  const selectedWorkflowSteps = Array.isArray(selectedWorkflowArtifact?.steps)
    ? selectedWorkflowArtifact.steps
    : [];
  const deviceExpertWorkflowJson = React.useMemo(() => {
    if (preparationSourceMode === 'draft' && workflowHasExecutableContent(draftWorkflowJson)) {
      return draftWorkflowJson;
    }
    if ((preparationSourceMode === 'approved' || replaceWorkflowSource) && selectedWorkflow) {
      return selectedWorkflowArtifact;
    }
    if (snapshot) {
      return workflowJson;
    }
    if (workflowHasExecutableContent(draftWorkflowJson)) {
      return draftWorkflowJson;
    }
    return selectedWorkflowArtifact;
  }, [
    draftWorkflowJson,
    preparationSourceMode,
    replaceWorkflowSource,
    selectedWorkflow,
    selectedWorkflowArtifact,
    snapshot,
    workflowJson
  ]);
  const deviceExpertModelId =
    caseRecord.device?.modelId ||
    selectedWorkflow?.deviceModelId ||
    selectedWorkflowArtifact?.deviceModelId ||
    workflowJson?.deviceModelId ||
    null;
  const deviceExpertOptions = React.useMemo(
    () => buildSafetyWorkflowDeviceExpertOptions(deviceExpertWorkflowJson),
    [deviceExpertWorkflowJson]
  );
  const selectedWorkflowSourceDocument = getWorkflowSourceDocument(selectedWorkflow);
  const draftWorkPlan = React.useMemo(
    () => buildWorkPlanSummary(draftWorkflowJson, { partCatalog, kitCatalog }),
    [draftWorkflowJson, kitCatalog, partCatalog]
  );
  const snapshotWorkPlan = React.useMemo(
    () => buildWorkPlanSummary(workflowJson, { partCatalog, kitCatalog }),
    [kitCatalog, partCatalog, workflowJson]
  );
  const snapshotTitle = snapshot?.title || workflowJson?.title || 'Manual safety / upgrade task';
  const lastSavedAt = snapshot?.updatedAt ? formatDateTime(snapshot.updatedAt) : '';
  const disabled = !canPrepare || Boolean(action);

  const openMediaAttachment = (item: any) => {
    const fileUrl = resolveFileUrl(baseUrl, item?.fileUrl);
    if (!fileUrl) {
      setError('This document is not available to open.');
      return;
    }
    setAttachmentViewer({
      ...item,
      url: fileUrl,
      fileName: item?.fileName || 'Attachment',
      mimeType: item?.mimeType || item?.fileType || null,
      fileType: item?.fileType || null,
      fileSize: item?.fileSize || null
    });
  };

  const openWorkflowSourceDocument = (workflow: any) => {
    const sourceDocument = getWorkflowSourceDocument(workflow);
    const fileUrl = resolveFileUrl(baseUrl, sourceDocument?.fileUrl);
    if (!fileUrl) {
      setError('This workflow source document is not available to open from the task workspace.');
      return;
    }
    setAttachmentViewer({
      ...sourceDocument,
      url: fileUrl,
      fileName: sourceDocument.fileName || workflow?.sourceLabel || 'Workflow source document',
      mimeType: sourceDocument.mimeType || null,
      fileType: sourceDocument.mimeType || null,
      fileSize: sourceDocument.fileSize || null
    });
  };

  const openWorkflowPreview = async (workflow: any) => {
    if (!workflow?.id) return;
    setAction(`preview:${workflow.id}`);
    setError('');
    try {
      const payload = await workflowProtocolsApi.getArtifact(workflow.id);
      setWorkflowViewer({
        record: workflow,
        artifact: payload?.artifact || workflow.workflowJson || {}
      });
    } catch (err: any) {
      const cachedArtifact = workflow.workflowJson || {};
      if (cachedArtifact && Object.keys(cachedArtifact).length > 0) {
        setWorkflowViewer({
          record: workflow,
          artifact: cachedArtifact
        });
      } else {
        setError(err?.message || 'Failed to open workflow preview.');
      }
    } finally {
      setAction('');
    }
  };

  const loadQuoteTemplateForQuote = async (quote: WorkflowPreparationQuote | null) => {
    const formsPayload = await formsApi.getAll();
    const templates = Array.isArray(formsPayload?.quotes) ? formsPayload.quotes : [];
    const templateId = String(quote?.templateId || '').trim();
    let selectedTemplate = templateId
      ? templates.find((template: any) => String(template?.id || '').trim() === templateId)
      : null;

    if (templateId && !selectedTemplate) {
      throw new Error('The saved quotation template for this quote is not available.');
    }

    if (!selectedTemplate) {
      selectedTemplate =
        (templates.length === 1
          ? templates[0]
          : templates.find((template: any) => template?.isPrimary) ||
            templates.find((template: any) => String(template?.name || '') === 'Default')) ||
        null;
    }

    if (!selectedTemplate) {
      throw new Error(
        'No quotation template is available. An admin must configure a quotation template in Documents settings.'
      );
    }

    return {
      template: selectedTemplate,
      layout: normalizeQuoteTemplateLayout(selectedTemplate)
    };
  };

  const openQuoteModal = async (mode: 'saved' | 'draft' | 'revision') => {
    if (!caseId || !snapshot?.id) return;
    setQuoteError('');
    setQuoteLoading(true);
    try {
      let nextQuote: WorkflowPreparationQuote | null = null;
      let nextRevisionSourceId: string | null = null;

      if (mode === 'saved' && displayPreparationQuote) {
        nextQuote = displayPreparationQuote;
      } else if (mode === 'revision' && displayPreparationQuote) {
        nextQuote = normalizePreparationQuote({
          ...displayPreparationQuote,
          quoteId: createWorkflowQuoteId(),
          status: 'DRAFT',
          customerDecision: null,
          decisionAt: null,
          decisionBy: null,
          savedAt: null
        });
        nextRevisionSourceId = displayPreparationQuote.quoteId;
      } else {
        const payload = await caseApi.createSafetyWorkflowQuoteDraft(caseId, {
          snapshotId: snapshot.id
        });
        const activeStatus = String(displayPreparationQuote?.status || '').trim().toUpperCase();
        nextQuote = normalizePreparationQuote(
          activeStatus && activeStatus !== 'DRAFT'
            ? { ...(payload?.quote || {}), quoteId: createWorkflowQuoteId(), status: 'DRAFT' }
            : payload?.quote
        );
      }

      if (!nextQuote) {
        throw new Error('Failed to build workflow quote.');
      }

      const { template, layout } = await loadQuoteTemplateForQuote(nextQuote);
      const quoteWithTemplate = normalizePreparationQuote({
        ...nextQuote,
        templateId: nextQuote.templateId || String(template?.id || ''),
        templateName: nextQuote.templateName || String(template?.name || 'Quote'),
        totalSettings: nextQuote.totalSettings || layout.totalSettings,
        responseActions: Array.isArray(nextQuote.responseActions)
          ? nextQuote.responseActions
          : layout.responseActions,
        approvalPlacement: nextQuote.approvalPlacement || layout.approvalPlacement,
        approvalFooterSlots: Array.isArray(nextQuote.approvalFooterSlots)
          ? nextQuote.approvalFooterSlots
          : layout.approvalFooterSlots,
        requireSignature:
          typeof nextQuote.requireSignature === 'boolean'
            ? nextQuote.requireSignature
            : layout.requireSignature,
        signatureLabel: nextQuote.signatureLabel || layout.signatureLabel,
        requireTechnicianSignature:
          typeof nextQuote.requireTechnicianSignature === 'boolean'
            ? nextQuote.requireTechnicianSignature
            : layout.requireTechnicianSignature,
        technicianSignatureLabel: nextQuote.technicianSignatureLabel || layout.technicianSignatureLabel
      });

      if (!quoteWithTemplate) {
        throw new Error('Failed to build workflow quote.');
      }
      const layoutForQuote = normalizeQuoteTemplateLayout({
        ...template,
        totalSettings: quoteWithTemplate.totalSettings || layout.totalSettings,
        responseActions: quoteWithTemplate.responseActions,
        approvalPlacement: quoteWithTemplate.approvalPlacement,
        approvalFooterSlots: quoteWithTemplate.approvalFooterSlots,
        requireSignature: quoteWithTemplate.requireSignature,
        signatureLabel: quoteWithTemplate.signatureLabel,
        requireTechnicianSignature: quoteWithTemplate.requireTechnicianSignature,
        technicianSignatureLabel: quoteWithTemplate.technicianSignatureLabel
      });

      setQuoteRevisionSourceId(nextRevisionSourceId);
      setQuoteTemplateLayout(layoutForQuote);
      setQuoteTemplateName(String(template?.name || quoteWithTemplate.templateName || 'Quote'));
      setQuoteDraft(quoteWithTemplate);
      setSelectedQuoteLineIds([]);
      setQuoteModalOpen(true);
    } catch (err: any) {
      setQuoteError(err?.message || 'Failed to build workflow quote.');
    } finally {
      setQuoteLoading(false);
    }
  };

  const updateQuoteDraft = (updater: (current: WorkflowPreparationQuote) => WorkflowPreparationQuote) => {
    setQuoteDraft(current => {
      const base =
        current ||
        normalizePreparationQuote({
          title: 'Safety / upgrade quote',
          description: '',
          lineItems: []
        });
      if (!base) return current;
      const next = updater(base);
      return {
        ...next,
        totalSettings: next.totalSettings || quoteTemplateLayout?.totalSettings || null,
        responseActions: Array.isArray(next.responseActions)
          ? next.responseActions
          : quoteTemplateLayout?.responseActions || [],
        approvalPlacement: next.approvalPlacement || quoteTemplateLayout?.approvalPlacement || 'footer_c3',
        approvalFooterSlots: Array.isArray(next.approvalFooterSlots)
          ? next.approvalFooterSlots
          : quoteTemplateLayout?.approvalFooterSlots || [],
        requireSignature:
          typeof next.requireSignature === 'boolean'
            ? next.requireSignature
            : (quoteTemplateLayout?.requireSignature ?? false),
        signatureLabel: next.signatureLabel || quoteTemplateLayout?.signatureLabel || 'Customer approval signature',
        requireTechnicianSignature:
          typeof next.requireTechnicianSignature === 'boolean'
            ? next.requireTechnicianSignature
            : (quoteTemplateLayout?.requireTechnicianSignature ?? false),
        technicianSignatureLabel:
          next.technicianSignatureLabel || quoteTemplateLayout?.technicianSignatureLabel || 'Technician signature',
        totals: computeQuoteTotals(next.lineItems, next.totalSettings || quoteTemplateLayout?.totalSettings)
      };
    });
  };

  const saveQuoteDraft = async () => {
    if (!caseId || !snapshot?.id || !quoteDraft) return;
    setQuoteSaving(true);
    setQuoteError('');
    try {
      const draftToSave =
        quoteDraft.quoteId && quoteDraft.quoteId.trim()
          ? quoteDraft
          : {
              ...quoteDraft,
              quoteId: createWorkflowQuoteId()
            };
      const payload = await caseApi.saveSafetyWorkflowQuote(caseId, {
        snapshotId: snapshot.id,
        quote: draftToSave,
        revisionOfQuoteId: quoteRevisionSourceId || undefined
      });
      const nextQuote = normalizePreparationQuote(payload?.quote || payload?.snapshot?.resultsJson?.preparationQuote);
      if (nextQuote) {
        setQuoteDraft(nextQuote);
      }
      setWorkspace((current: any) => ({
        ...(current || {}),
        snapshot: payload?.snapshot || current?.snapshot || null,
        quotes: Array.isArray(payload?.quotes) ? payload.quotes : current?.quotes || []
      }));
      setQuoteModalOpen(false);
      setQuoteRevisionSourceId(null);
      await loadWorkspace();
    } catch (err: any) {
      setQuoteError(err?.message || 'Failed to save workflow quote.');
    } finally {
      setQuoteSaving(false);
    }
  };

  const sendQuote = async () => {
    if (!caseId || !displayPreparationQuote?.quoteId) return;
    setQuoteSending(true);
    setQuoteError('');
    try {
      const payload = await caseApi.sendSafetyWorkflowQuote(caseId, displayPreparationQuote.quoteId);
      setWorkspace((current: any) => ({
        ...(current || {}),
        quotes: Array.isArray(payload?.quotes) ? payload.quotes : current?.quotes || []
      }));
      await loadWorkspace();
    } catch (err: any) {
      setQuoteError(err?.message || 'Failed to send workflow quote.');
    } finally {
      setQuoteSending(false);
    }
  };

  const setQuoteActive = async (
    quote: WorkflowPreparationQuote,
    active: boolean,
    reasonInput = ''
  ) => {
    if (!caseId || !quote?.quoteId) return false;
    const status = normaliseWorkflowQuoteStatus(quote?.status);
    const inactiveFromStatus = normaliseWorkflowQuoteStatus(quote?.inactiveFromStatus);
    if (!active && status !== 'SENT') return false;
    if (active && (status !== 'INACTIVE' || inactiveFromStatus !== 'SENT')) return false;

    const reason = String(reasonInput || '').trim();
    if (!active && !reason) {
      setQuoteInactivationError('Reason is required.');
      return false;
    }

    try {
      setQuoteInactivationError(null);
      setQuoteError('');
      setQuoteStateChangeId(quote.quoteId);
      const payload = await caseApi.setSafetyWorkflowQuoteActive(caseId, quote.quoteId, {
        active,
        reason: reason || undefined
      });
      setWorkspace((current: any) => ({
        ...(current || {}),
        quotes: Array.isArray(payload?.quotes) ? payload.quotes : current?.quotes || []
      }));
      await loadWorkspace();
      return true;
    } catch (err: any) {
      const message = err?.message || `Failed to ${active ? 'reactivate' : 'inactivate'} quote.`;
      setQuoteError(message);
      setQuoteInactivationError(message);
      return false;
    } finally {
      setQuoteStateChangeId(null);
    }
  };

  const openQuoteInactivateModal = (quote: WorkflowPreparationQuote) => {
    setQuoteInactivateTarget(quote);
    setQuoteInactivationReason('');
    setQuoteInactivationError(null);
    setQuoteError('');
  };

  const closeQuoteInactivateModal = () => {
    if (quoteStateChangeId) return;
    setQuoteInactivateTarget(null);
    setQuoteInactivationReason('');
    setQuoteInactivationError(null);
  };

  const confirmQuoteInactivate = async () => {
    if (!quoteInactivateTarget) return;
    const reason = quoteInactivationReason.trim();
    if (!reason) {
      setQuoteInactivationError('Reason is required.');
      return;
    }
    const ok = await setQuoteActive(quoteInactivateTarget, false, reason);
    if (ok) {
      setQuoteInactivateTarget(null);
      setQuoteInactivationReason('');
      setQuoteInactivationError(null);
    }
  };

  const openQuoteDeleteModal = (quote: WorkflowPreparationQuote) => {
    const status = normaliseWorkflowQuoteStatus(quote?.status);
    if (status !== 'DRAFT') {
      setQuoteError('Only draft quotes can be deleted.');
      return;
    }
    setQuoteDeleteTarget(quote);
    setQuoteDeleteError(null);
    setQuoteError('');
  };

  const closeQuoteDeleteModal = () => {
    if (quoteDeleteId) return;
    setQuoteDeleteTarget(null);
    setQuoteDeleteError(null);
  };

  const confirmQuoteDelete = async () => {
    if (!caseId || !quoteDeleteTarget?.quoteId) return;
    const status = normaliseWorkflowQuoteStatus(quoteDeleteTarget?.status);
    if (status !== 'DRAFT') {
      setQuoteDeleteError('Only draft quotes can be deleted.');
      return;
    }

    try {
      setQuoteDeleteError(null);
      setQuoteError('');
      setQuoteDeleteId(quoteDeleteTarget.quoteId);
      const payload = await caseApi.deleteSafetyWorkflowQuote(caseId, quoteDeleteTarget.quoteId);
      setWorkspace((current: any) => ({
        ...(current || {}),
        quotes: Array.isArray(payload?.quotes) ? payload.quotes : current?.quotes || []
      }));
      setQuoteDeleteTarget(null);
      await loadWorkspace();
    } catch (err: any) {
      const message = err?.message || 'Failed to delete draft quote.';
      setQuoteError(message);
      setQuoteDeleteError(message);
    } finally {
      setQuoteDeleteId(null);
    }
  };

  const quoteLineItemsForModal = React.useMemo<QuoteLineItemDraft[]>(
    () => {
      const lineItems = quoteDraft && Array.isArray(quoteDraft.lineItems) ? quoteDraft.lineItems : [];
      return lineItems.map(line => ({ ...line }));
    },
    [quoteDraft]
  );
  const quoteTotalsForModal =
    quoteDraft?.totals || computeQuoteTotals(quoteDraft?.lineItems || [], quoteDraft?.totalSettings || quoteTemplateLayout?.totalSettings);
  const quoteUnmatchedPartsForModal = quoteDraft?.unmatchedParts || [];
  const quoteHasActiveSla = Boolean(
    quoteDraft?.slaCoverage?.active ||
      quoteLineItemsForModal.some(
        line =>
          line.slaCoverageStatus === 'COVERED' ||
          line.slaCoverageStatus === 'DISCOUNTED' ||
          Boolean(line.slaLabel)
      )
  );

  const setQuoteTitleForModal: React.Dispatch<React.SetStateAction<string>> = next => {
    updateQuoteDraft(current => ({
      ...current,
      title: typeof next === 'function' ? next(current.title) : next
    }));
  };

  const setQuoteDescriptionForModal: React.Dispatch<React.SetStateAction<string>> = next => {
    updateQuoteDraft(current => ({
      ...current,
      description: typeof next === 'function' ? next(current.description) : next
    }));
  };

  const setQuoteNotesForModal: React.Dispatch<React.SetStateAction<string>> = next => {
    updateQuoteDraft(current => {
      const currentNotes = current.notes || '';
      return {
        ...current,
        notes: typeof next === 'function' ? next(currentNotes) : next
      };
    });
  };

  const setQuoteCustomerNameForModal: React.Dispatch<React.SetStateAction<string>> = next => {
    updateQuoteDraft(current => {
      const currentName = current.customerApprovalName || '';
      return {
        ...current,
        customerApprovalName: typeof next === 'function' ? next(currentName) : next
      };
    });
  };

  const setQuoteCustomerSignatureForModal: React.Dispatch<React.SetStateAction<string>> = next => {
    updateQuoteDraft(current => {
      const currentSignature = current.customerApprovalSignature || '';
      return {
        ...current,
        customerApprovalSignature: typeof next === 'function' ? next(currentSignature) : next
      };
    });
  };

  const setQuoteLineItemsForModal: React.Dispatch<React.SetStateAction<QuoteLineItemDraft[]>> = next => {
    updateQuoteDraft(current => {
      const currentLines = current.lineItems.map(line => ({ ...line }));
      const resolvedLines = typeof next === 'function' ? next(currentLines) : next;
      return {
        ...current,
        lineItems: resolvedLines.map((line, index) => normalizeQuoteLineItem(line, index))
      };
    });
  };

  const applyPartNumberToQuoteLine = (index: number, value: string) => {
    const partNumber = String(value || '').trim();
    const match = quotePartCatalog.find(item => {
      const candidates = [item.partNumber, item.sku].map(candidate => String(candidate || '').trim().toLowerCase());
      return candidates.includes(partNumber.toLowerCase());
    });
    setQuoteLineItemsForModal(current =>
      current.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        if (!match) {
          return {
            ...line,
            partNumber,
            sku: null,
            catalogId: null,
            source: 'manual'
          };
        }
        return {
          ...line,
          lineType: 'part',
          partNumber: match.partNumber || match.sku || partNumber,
          sku: match.sku,
          catalogId: match.id,
          description: match.description || line.description,
          unitValue: match.unitValue,
          isFree: false,
          source: 'catalog',
          originalUnitValue: null,
          slaCoverageStatus: null,
          slaLabel: null,
          slaDiscountPercent: null,
          slaCoverageReason: null,
          slaMatchedListNames: []
        };
      })
    );
  };

  const renderHero = () => (
    <section style={styles.caseHeaderCard}>
      <div style={styles.caseHeaderTop}>
        <div style={styles.caseHeaderCopy}>
          <h1 style={styles.caseHeroTitle}>Workflow Preparation</h1>
          <div style={styles.caseHeroProblem}>{problemLabel}</div>
        </div>
        <div style={styles.caseHeroActions}>
          <button
            type="button"
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={() => void loadWorkspace()}
          >
            Reload
          </button>
        </div>
      </div>
      <div style={styles.caseHeaderGrid}>
        <Summary label="Case" value={caseLabel} />
        <Summary label="Customer" value={customerLabel} />
        <Summary label="Device" value={deviceLabel} />
        <Summary label="Task" value={taskLabel} />
        <Summary
          label="Notice documents"
          value={media.length > 0 ? `${media.length} attached` : 'None attached'}
        />
      </div>
      <div style={styles.casePersistenceNote}>
        Prepared workflows are saved on the case itself, remain available to later assigned users, and are not
        rewritten by later Documents workflow changes.
      </div>
    </section>
  );

  const renderCreateWorkflow = () => {
    const draftReady = workflowHasExecutableContent(draftWorkflowJson);
    const showDraftEditor = draftMethod === 'manual' || draftEditing;

    const clearDraftWorkflow = () => {
      setGeneratedDraft(null);
      setDraftEditing(false);
      setDraftMethod('generate');
      setDraftWorkflowJson(createEmptyWorkflowJson({ category: 'SAFETY_UPGRADE' }));
      if (snapshot) {
        setPreparationSourceMode('approved');
        setReplaceWorkflowSource(false);
      }
    };

    const startWorkflowReplacement = () => {
      setReplaceWorkflowSource(true);
      setPreparationSourceMode('approved');
      setSelectedWorkflowId('');
      setSearch('');
      setGeneratedDraft(null);
      setDraftEditing(false);
      setDraftMethod('generate');
      setDraftWorkflowJson(createEmptyWorkflowJson({ category: 'SAFETY_UPGRADE' }));
    };

    const renderPreparedWorkflow = () => (
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.sectionTitle}>Prepared case workflow</div>
            <div style={styles.sectionSub}>{snapshotTitle}</div>
          </div>
          <div style={styles.actionRow}>
            <button
              type="button"
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={loadSnapshotForEditing}
              disabled={disabled}
            >
              Edit case workflow
            </button>
            <button
              type="button"
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={startWorkflowReplacement}
              disabled={disabled}
            >
              Replace case workflow
            </button>
          </div>
        </div>
        <div style={styles.contextStrip}>
          This saved workflow is the active case copy for this safety / upgrade job. Later assigned users can open
          the same prepared workflow, and later Documents changes do not overwrite this case copy.
        </div>
        <WorkPlanSummaryPanel summary={snapshotWorkPlan} />
        <TaskDeviceExpertPanel
          modelId={deviceExpertModelId}
          parentOptions={deviceExpertOptions}
          taskLabel="safety / upgrade preparation"
          parentLabel="Workflow step"
          contextLabel="Checklist item"
          subtitle="Ask about a selected workflow step for contextual help, or ask an open device question."
          noParentLabel="No specific workflow step selected"
          noContextLabel="No checklist item selected"
          selectParentFirstLabel="Select a workflow step first"
          questionPlaceholder="Ask about the prepared workflow, or leave blank to get more detail on the selected checklist item."
          contextHint="Choosing a checklist item adds that workflow context. With no step selected, your question is treated as an open Device Expert ask."
          unavailableText="This safety / upgrade task has no device-model context yet, so Device Expert is unavailable."
          emptyContextText="No workflow steps are available for Device Expert context yet."
        />
        <WorkflowQuotePanel
          quote={displayPreparationQuote}
          loading={quoteLoading}
          saving={quoteSaving}
          sending={quoteSending}
          opening={quoteLoading}
          onCreate={() => void openQuoteModal('draft')}
          onEdit={() => void openQuoteModal('saved')}
          onRefresh={() => void openQuoteModal('draft')}
          onSend={() => void sendQuote()}
          onCreateRevision={() => void openQuoteModal('revision')}
          onOpen={() => void openQuoteModal('saved')}
          onInactivate={() => displayPreparationQuote && openQuoteInactivateModal(displayPreparationQuote)}
          onReactivate={() => displayPreparationQuote && setQuoteActive(displayPreparationQuote, true)}
          onDelete={() => displayPreparationQuote && openQuoteDeleteModal(displayPreparationQuote)}
          disabled={disabled}
          error={quoteError}
        />
        <WorkflowProtocolPreview workflowJson={workflowJson} />
      </section>
    );

    const saveDraftToCase = () =>
      void saveWorkflowDraft(
        generatedDraft ? 'CASE_GENERATED_WORKFLOW' : 'CASE_MANUAL_WORKFLOW',
        'save-case'
      );

    const renderDraftWorkflow = () => (
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.sectionTitle}>{snapshot ? 'Edited case workflow' : 'Draft case workflow'}</div>
            <div style={styles.sectionSub}>{draftTitle || 'Unsaved workflow draft'}</div>
          </div>
          <div style={styles.actionRow}>
            {draftMethod === 'generate' ? (
              <button
                type="button"
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setDraftEditing(current => !current)}
                disabled={disabled}
              >
                {draftEditing ? 'Show preview' : 'Edit workflow'}
              </button>
            ) : null}
          </div>
        </div>
        <CaseWorkflowSaveBar
          disabled={disabled}
          loading={action === 'save-case'}
          saveLabel={snapshot ? 'Save case workflow changes' : 'Save case workflow'}
          helperText="Saving here updates the case workflow immediately and also sends a draft to Documents for admin review and wider reuse."
          onSave={saveDraftToCase}
          onCancel={clearDraftWorkflow}
          cancelLabel={snapshot ? 'Discard local edits' : 'Cancel draft'}
        />

        <WorkPlanSummaryPanel summary={draftWorkPlan} />
        <TaskDeviceExpertPanel
          modelId={deviceExpertModelId}
          parentOptions={deviceExpertOptions}
          taskLabel="safety / upgrade preparation"
          parentLabel="Workflow step"
          contextLabel="Checklist item"
          subtitle="Ask about a selected workflow step for contextual help, or ask an open device question."
          noParentLabel="No specific workflow step selected"
          noContextLabel="No checklist item selected"
          selectParentFirstLabel="Select a workflow step first"
          questionPlaceholder="Ask about the draft workflow, or leave blank to get more detail on the selected checklist item."
          contextHint="Choosing a checklist item adds that workflow context. With no step selected, your question is treated as an open Device Expert ask."
          unavailableText="This safety / upgrade task has no device-model context yet, so Device Expert is unavailable."
          emptyContextText="No workflow steps are available for Device Expert context yet."
        />

        {showDraftEditor ? (
          <div style={styles.field}>
            <span style={styles.formLabel}>Workflow content</span>
            <WorkflowProtocolEditor
              value={draftWorkflowJson}
              onChange={setDraftWorkflowJson}
              disabled={disabled}
            />
          </div>
        ) : (
          <WorkflowProtocolPreview workflowJson={draftWorkflowJson} />
        )}
      </section>
    );

    const renderWorkflowSource = () => (
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <div style={styles.sectionTitle}>Case workflow source</div>
            <div style={styles.sectionSub}>
              {snapshot ? 'Choose a replacement source for this case workflow.' : 'Choose one source for this case workflow.'}
            </div>
          </div>
          <span style={styles.statusChip}>{snapshot ? 'Replacing workflow' : 'Not prepared'}</span>
        </div>
        <div style={styles.contextStrip}>
          Any workflow prepared here is stored on this case and stays available to later assigned users. Case-created
          workflows are also sent to Documents as drafts for admin review.
        </div>

        <div style={styles.sourceModeGrid}>
          <button
            type="button"
            style={{
              ...styles.sourceModeCard,
              ...(preparationSourceMode === 'approved' ? styles.sourceModeCardActive : {})
            }}
            onClick={() => setPreparationSourceMode('approved')}
          >
            <span style={styles.sourceModeTitle}>Use approved workflow</span>
            <span style={styles.sourceModeMeta}>Load an admin-approved workflow into this case as the active case copy.</span>
          </button>
          <button
            type="button"
            style={{
              ...styles.sourceModeCard,
              ...(preparationSourceMode === 'draft' ? styles.sourceModeCardActive : {})
            }}
            onClick={() => setPreparationSourceMode('draft')}
          >
            <span style={styles.sourceModeTitle}>Create case workflow</span>
            <span style={styles.sourceModeMeta}>Generate from the notice or build manually for this case.</span>
          </button>
        </div>

        {preparationSourceMode === 'approved' ? (
          <>
            <div style={styles.compactSourceGrid}>
              <label style={styles.field}>
                <span style={styles.formLabel}>Filter workflows</span>
                <input
                  style={styles.input}
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Title, model, source"
                />
              </label>
              <label style={styles.field}>
                <span style={styles.formLabel}>Approved workflow</span>
                <select
                  style={styles.input}
                  value={selectedWorkflowId}
                  onChange={event => setSelectedWorkflowId(event.target.value)}
                >
                  <option value="">
                    {workflows.length === 0 ? 'No approved workflows available' : 'Select approved workflow...'}
                  </option>
                  {approvedWorkflowOptions.map((workflow: any) => (
                    <option key={workflow.id} value={workflow.id}>
                      {workflow.title} {workflow.version ? `(v${workflow.version})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {selectedWorkflow ? (
              <div style={styles.sourceCard}>
                <div style={styles.sourceCardTitle}>Selected approved workflow</div>
                <div style={styles.sourceCardMain}>{selectedWorkflow.title}</div>
                <div style={styles.sourceCardMeta}>
                  {[
                    selectedWorkflow.deviceModelLabel || 'All models',
                    selectedWorkflow.version ? `v${selectedWorkflow.version}` : '',
                    `${selectedWorkflowSteps.length} steps`,
                    selectedWorkflowSourceDocument?.fileName
                      ? `Source: ${selectedWorkflowSourceDocument.fileName}`
                      : selectedWorkflow.sourceLabel
                      ? `Source: ${selectedWorkflow.sourceLabel}`
                      : ''
                  ]
                    .filter(Boolean)
                    .join(' | ')}
                </div>
                <div style={styles.actionRow}>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.primaryButton }}
                    onClick={() => void loadApprovedWorkflow()}
                    disabled={disabled}
                  >
                    {action === 'select' ? 'Loading...' : snapshot ? 'Replace case workflow' : 'Use for this case'}
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    onClick={() => void openWorkflowPreview(selectedWorkflow)}
                    disabled={Boolean(action)}
                  >
                    {action === `preview:${selectedWorkflow.id}` ? 'Opening...' : 'View workflow'}
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    onClick={() => openWorkflowSourceDocument(selectedWorkflow)}
                    disabled={!selectedWorkflowSourceDocument?.fileUrl}
                  >
                    Open source
                  </button>
                  {snapshot ? (
                    <button
                      type="button"
                      style={{ ...styles.button, ...styles.secondaryButton }}
                      onClick={() => setReplaceWorkflowSource(false)}
                      disabled={disabled}
                    >
                      Cancel replacement
                    </button>
                  ) : null}
                </div>
              </div>
            ) : workflows.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyTitle}>No approved workflows available</div>
                <div style={styles.emptyCopy}>
                  Create a workflow from the case notice or build one manually for this case.
                </div>
              </div>
            ) : snapshot ? (
              <div style={styles.actionRow}>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={() => setReplaceWorkflowSource(false)}
                  disabled={disabled}
                >
                  Cancel replacement
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <fieldset disabled={!canPrepare} style={styles.fieldset}>
            <div style={styles.sourceModeGrid}>
              <button
                type="button"
                style={{
                  ...styles.sourceModeCard,
                  ...(draftMethod === 'generate' ? styles.sourceModeCardActive : {})
                }}
                onClick={() => {
                  setDraftMethod('generate');
                  setDraftEditing(false);
                }}
              >
                <span style={styles.sourceModeTitle}>Generate from notice</span>
                <span style={styles.sourceModeMeta}>Use the attached notice and device knowledge.</span>
              </button>
              <button
                type="button"
                style={{
                  ...styles.sourceModeCard,
                  ...(draftMethod === 'manual' ? styles.sourceModeCardActive : {})
                }}
                onClick={() => {
                  setDraftMethod('manual');
                  setGeneratedDraft(null);
                  setDraftEditing(true);
                }}
              >
                <span style={styles.sourceModeTitle}>Build manually</span>
                <span style={styles.sourceModeMeta}>Write the workflow directly for this case.</span>
              </button>
            </div>

            {draftMethod === 'generate' ? (
              <div style={styles.sourceCard}>
                <div style={styles.sourceCardTitle}>Case notice document</div>
                <label style={styles.field}>
                  <span style={styles.formLabel}>Notice</span>
                  <select
                    style={styles.input}
                    value={selectedMediaId}
                    onChange={event => setSelectedMediaId(event.target.value)}
                  >
                    {media.length === 0 ? (
                      <option value="">No case documents attached</option>
                    ) : (
                      media.map((item: any) => (
                        <option key={item.id} value={item.id}>
                          {item.fileName || 'Attached document'}{' '}
                          {formatFileSize(item.fileSize) ? `| ${formatFileSize(item.fileSize)}` : ''}
                        </option>
                      ))
                    )}
                  </select>
                </label>
                {selectedMedia ? (
                  <div style={styles.sourceCardMeta}>
                    {[selectedMedia.mimeType || selectedMedia.fileType, formatFileSize(selectedMedia.fileSize)]
                      .filter(Boolean)
                      .join(' | ')}
                  </div>
                ) : null}
                <div style={styles.actionRow}>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    onClick={() => openMediaAttachment(selectedMedia)}
                    disabled={!selectedMedia}
                  >
                    Open notice
                  </button>
                </div>
              </div>
            ) : null}

            <div style={styles.formGrid}>
              <label style={styles.field}>
                <span style={styles.formLabel}>Working title</span>
                <input
                  style={styles.input}
                  value={draftTitle}
                  onChange={event => setDraftTitle(event.target.value)}
                  placeholder="Workflow title"
                />
              </label>
              <label style={styles.field}>
                <span style={styles.formLabel}>Additional context</span>
                <textarea
                  style={{ ...styles.textarea, minHeight: '4.5rem' }}
                  value={draftDescription}
                  onChange={event => setDraftDescription(event.target.value)}
                  placeholder="Optional context for the generator or manual workflow."
                />
              </label>
            </div>

            {draftMethod === 'generate' ? (
              <div style={styles.actionRow}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => void createWorkflowDraft()}
                disabled={disabled || !selectedMediaId}
              >
                {action === 'generate' ? 'Creating...' : 'Create Draft Workflow'}
              </button>
            </div>
          ) : null}

            {showDraftEditor ? (
              <div style={styles.field}>
                <span style={styles.formLabel}>Workflow content</span>
                <WorkflowProtocolEditor
                  value={draftWorkflowJson}
                  onChange={setDraftWorkflowJson}
                  disabled={disabled}
                />
              </div>
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyTitle}>No workflow drafted yet</div>
                <div style={styles.emptyCopy}>
                  Generate from the selected notice or choose Build manually to write the workflow.
                </div>
              </div>
            )}
          </fieldset>
        )}
      </section>
    );

    if (draftReady) return renderDraftWorkflow();
    if (snapshot && !replaceWorkflowSource) return renderPreparedWorkflow();
    return renderWorkflowSource();
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.section}>
            <div style={styles.muted}>Loading safety / upgrade workspace...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.pageHeader}>
          <button type="button" style={styles.backButton} onClick={() => navigate('/tech')}>
            Back to cases
          </button>
          <div style={styles.headerActions}>
            {lastSavedAt ? <span style={styles.savedAt}>Last saved {lastSavedAt}</span> : null}
          </div>
        </div>

        {!canPrepare ? (
          <div style={styles.readOnlyBanner}>
            This safety / upgrade task is not active for editing. The workspace can still be reviewed.
          </div>
        ) : null}

        {error ? <div style={styles.error}>{error}</div> : null}

        {renderHero()}
        {renderCreateWorkflow()}
      </div>
      <CaseAttachmentViewerModal
        attachment={attachmentViewer}
        onClose={() => setAttachmentViewer(null)}
        formatFileSize={formatFileSize}
      />
      {workflowViewer ? (
        <WorkflowPreviewModal
          viewer={workflowViewer}
          onClose={() => setWorkflowViewer(null)}
        />
      ) : null}
      {quoteModalOpen && quoteDraft && quoteTemplateLayout ? (
        <TSFADQuoteModal
          open={quoteModalOpen}
          styles={styles}
          quoteHasActiveSla={quoteHasActiveSla}
          quoteTemplateName={quoteTemplateName}
          quoteEditingQuoteId={normaliseWorkflowQuoteStatus(quoteDraft.status) === 'DRAFT' ? quoteDraft.quoteId : null}
          quoteRevisionOfQuoteId={quoteRevisionSourceId}
          quoteHeaderBlocks={quoteHeaderBlocks}
          quoteTitle={quoteDraft.title}
          setQuoteTitle={setQuoteTitleForModal}
          quoteDescription={quoteDraft.description}
          setQuoteDescription={setQuoteDescriptionForModal}
          quoteLineItems={quoteLineItemsForModal}
          setQuoteLineItems={setQuoteLineItemsForModal}
          selectedQuoteLineIds={selectedQuoteLineIds}
          setSelectedQuoteLineIds={setSelectedQuoteLineIds}
          applyPartNumberToQuoteLine={applyPartNumberToQuoteLine}
          quotePartCatalog={quotePartCatalog}
          quoteTotals={quoteTotalsForModal}
          quoteUnmatchedParts={quoteUnmatchedPartsForModal}
          quoteNotes={quoteDraft.notes || ''}
          setQuoteNotes={setQuoteNotesForModal}
          quoteTemplateLayout={quoteTemplateLayout}
          quoteCustomerName={quoteDraft.customerApprovalName || ''}
          setQuoteCustomerName={setQuoteCustomerNameForModal}
          quoteCustomerSignature={quoteDraft.customerApprovalSignature || ''}
          setQuoteCustomerSignature={setQuoteCustomerSignatureForModal}
          quoteSaving={quoteSaving}
          quoteSaveButtonLabel="Save Quote"
          onClose={() => {
            if (quoteSaving) return;
            setQuoteModalOpen(false);
            setQuoteError('');
            setQuoteRevisionSourceId(null);
            setQuoteTemplateLayout(null);
            setQuoteTemplateName(null);
            setSelectedQuoteLineIds([]);
            setQuoteDraft(displayPreparationQuote);
          }}
          onSave={() => void saveQuoteDraft()}
        />
      ) : null}
      <TSFADQuoteInactivateModal
        open={Boolean(quoteInactivateTarget)}
        styles={styles}
        quoteInactivateTarget={quoteInactivateTarget}
        quoteInactivationReason={quoteInactivationReason}
        quoteInactivationError={quoteInactivationError}
        quoteStateChangeId={quoteStateChangeId}
        onReasonChange={value => {
          setQuoteInactivationReason(value);
          if (quoteInactivationError) setQuoteInactivationError(null);
        }}
        onClose={closeQuoteInactivateModal}
        onConfirm={confirmQuoteInactivate}
      />
      <TSFADQuoteDeleteModal
        open={Boolean(quoteDeleteTarget)}
        styles={styles}
        quoteDeleteTarget={quoteDeleteTarget}
        quoteDeleteError={quoteDeleteError}
        quoteDeleteId={quoteDeleteId}
        onClose={closeQuoteDeleteModal}
        onConfirm={confirmQuoteDelete}
      />
    </div>
  );
};

const PrepList: React.FC<{ title: string; items: string[]; empty: string }> = ({ title, items, empty }) => (
  <div style={styles.prepList}>
    <div style={styles.fieldLabel}>{title}</div>
    {items.length > 0 ? (
      <div style={styles.prepItems}>
        {items.map(item => (
          <span key={item} style={styles.prepChip}>{item}</span>
        ))}
      </div>
    ) : (
      <div style={styles.sourceCardMeta}>{empty}</div>
    )}
  </div>
);

const WorkPlanSummaryPanel: React.FC<{ summary: WorkPlanSummary }> = ({ summary }) => (
  <div style={styles.workPlanPanel}>
    <div style={styles.sectionTitle}>Work Plan Summary</div>
    <div style={styles.workPlanStats}>
      <div style={styles.workPlanMetric}>
        <div style={styles.workPlanMetricValue}>
          {summary.estimatedMinutes ? summary.estimatedMinutes : '-'}
        </div>
        <div style={styles.sourceCardMeta}>Estimated Minutes On Site</div>
      </div>
      <div style={styles.workPlanMetric}>
        <div style={styles.workPlanMetricValue}>{summary.stepCount}</div>
        <div style={styles.sourceCardMeta}>Workflow Steps</div>
      </div>
      <div style={styles.workPlanMetric}>
        <div style={styles.workPlanMetricValue}>{summary.parts.length}</div>
        <div style={styles.sourceCardMeta}>Parts To Take</div>
      </div>
    </div>
    <div style={styles.prepGrid}>
      <PrepList title="Required special tools" items={summary.tools} empty="No special tools listed." />
      <PrepList title="Required parts" items={summary.parts} empty="No required parts listed." />
    </div>
  </div>
);

const WorkflowQuotePanel: React.FC<{
  quote: WorkflowPreparationQuote | null;
  loading: boolean;
  saving: boolean;
  sending: boolean;
  opening: boolean;
  disabled: boolean;
  error: string;
  onCreate: () => void;
  onEdit: () => void;
  onRefresh: () => void;
  onSend: () => void;
  onCreateRevision: () => void;
  onOpen: () => void;
  onInactivate: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}> = ({
  quote,
  loading,
  saving,
  sending,
  opening,
  disabled,
  error,
  onCreate,
  onEdit,
  onRefresh,
  onSend,
  onCreateRevision,
  onOpen,
  onInactivate,
  onReactivate,
  onDelete
}) => {
  const slaActive = Boolean(quote?.slaCoverage?.active);
  const slaLabel = slaActive
    ? quote?.slaCoverage?.template?.brandingName ||
      quote?.slaCoverage?.template?.name ||
      'Active SLA'
    : 'No active SLA';
  const safeQuote: WorkflowPreparationQuote = quote || ({
    quoteId: '',
    title: '',
    description: '',
    status: '',
    lineItems: [],
    totals: {
      parts: 0,
      labor: 0,
      subtotal: 0,
      discountTotal: 0,
      taxableTotal: 0,
      taxTotal: 0,
      grandTotal: 0
    },
    totalSettings: null,
    responseActions: ['accept', 'deny', 'adjust'],
    approvalPlacement: 'footer_c3',
    approvalFooterSlots: ['footer_c3'],
    requireSignature: false,
    signatureLabel: 'Customer approval signature',
    requireTechnicianSignature: false,
    technicianSignatureLabel: 'Technician signature',
    unmatchedParts: [],
    pricingUpdatedAt: null,
    savedAt: null,
    slaCoverage: null,
    laborCoverage: null
  } as WorkflowPreparationQuote);
  const status = String(safeQuote?.status || '').trim().toUpperCase();
  const isDraft = status === 'DRAFT';
  const isAwaitingCustomer = status === 'SENT';
  const isInactive = status === 'INACTIVE';
  const canCreateRevision = status === 'ADJUST_REQUESTED';
  const canCreateFreshDraft = !quote || status === 'DENIED' || status === 'ACCEPTED' || status === 'INACTIVE';
  const canInactivate = isAwaitingCustomer && !quote?.customerDecision;
  const canReactivate = isInactive && normaliseWorkflowQuoteStatus(quote?.inactiveFromStatus) === 'SENT';
  const statusChipStyle = getWorkflowQuoteStatusBadgeStyle(safeQuote);
  const decisionLabel = getWorkflowQuoteDecisionLabel(safeQuote);
  const decisionBadgeStyle = getWorkflowQuoteDecisionBadgeStyle(safeQuote);
  const showDecisionChip = Boolean(decisionLabel) && !['ACCEPTED', 'DENIED', 'ADJUST_REQUESTED'].includes(status);
  const quoteStatusLabel = getWorkflowQuoteStatusLabel(safeQuote);
  const quoteSavedAt = quote?.savedAt || quote?.occurredAt || '';
  const quoteTitle = String(quote?.title || '').trim() || 'Safety / upgrade quote';
  const quoteTotal = formatMoney(quote?.totals?.grandTotal || 0);

  return (
    <div style={styles.quotePanel}>
      <div style={styles.sectionHeader}>
        <div>
          <div style={styles.sectionTitle}>Saved Quotes ({quote ? 1 : 0})</div>
        </div>
        <div style={styles.actionRow}>
          {!quote ? (
            <button
              type="button"
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={onCreate}
              disabled={disabled || loading || saving || sending}
            >
              {loading ? 'Creating...' : 'Create Quotation'}
            </button>
          ) : canCreateFreshDraft ? (
            <button
              type="button"
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={onCreate}
              disabled={disabled || loading || saving || sending}
            >
              {loading ? 'Creating...' : 'Create new draft'}
            </button>
          ) : null}
        </div>
      </div>
      {error ? <div style={styles.inlineError}>{error}</div> : null}
      {quote ? (
        <div style={styles.savedQuoteRow}>
          <div style={styles.savedQuoteMain}>
            <div style={styles.savedQuoteTitleRow}>
              <span style={styles.savedQuoteTitle}>{quoteTitle}</span>
              <span style={{ ...styles.statusChip, ...statusChipStyle }}>{quoteStatusLabel}</span>
              {showDecisionChip ? <span style={{ ...styles.statusChip, ...decisionBadgeStyle }}>{decisionLabel}</span> : null}
            </div>
            <div style={styles.savedQuoteMeta}>
              {quoteSavedAt ? formatDateTime(quoteSavedAt) : 'Saved'} | Total {quoteTotal} | {slaLabel}
            </div>
            {quote.customerDecision ? (
              <div style={{ ...styles.savedQuoteDecision, color: decisionBadgeStyle.color || BRAND_TEXT }}>
                Decision: {quote.customerDecision}
                {quote.decisionAt ? ` (${formatDateTime(quote.decisionAt)})` : ''}
                {quote.decisionBy ? ` by ${quote.decisionBy}` : ''}
              </div>
            ) : null}
            {isInactive && quote?.inactiveReason ? (
              <div style={styles.savedQuoteDecision}>Inactive reason: {quote.inactiveReason}</div>
            ) : null}
            {quote.unmatchedParts.length > 0 ? (
              <div style={styles.savedQuoteDecision}>
                Pricing needs manual review for: {quote.unmatchedParts.join(', ')}
              </div>
            ) : null}
          </div>
          <div style={styles.savedQuoteActions}>
            {isDraft ? (
              <>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondaryButton, ...styles.compactButton }}
                  onClick={onRefresh}
                  disabled={disabled || loading || saving || sending}
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.primaryButton, ...styles.compactButton }}
                  onClick={onSend}
                  disabled={disabled || loading || saving || sending}
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.dangerButton, ...styles.compactButton }}
                  onClick={onDelete}
                  disabled={disabled || loading || saving || sending}
                >
                  {saving ? 'Deleting...' : 'Delete'}
                </button>
              </>
            ) : null}
            {canCreateRevision ? (
              <button
                type="button"
                style={{ ...styles.button, ...styles.primaryButton, ...styles.compactButton }}
                onClick={onCreateRevision}
                disabled={disabled || loading || saving || sending}
              >
                Revise
              </button>
            ) : null}
            {canInactivate ? (
              <button
                type="button"
                style={{ ...styles.button, ...styles.warningButton, ...styles.compactButton }}
                onClick={onInactivate}
                disabled={disabled || loading || saving || sending}
              >
                Inactivate
              </button>
            ) : null}
            {canReactivate ? (
              <button
                type="button"
                style={{ ...styles.button, ...styles.primaryButton, ...styles.compactButton }}
                onClick={onReactivate}
                disabled={disabled || loading || saving || sending}
              >
                Activate
              </button>
            ) : null}
            <button
              type="button"
              style={{ ...styles.button, ...styles.secondaryButton, ...styles.compactButton }}
              onClick={onOpen}
              disabled={disabled || opening || loading || saving || sending}
            >
              {opening ? 'Opening...' : 'Open'}
            </button>
          </div>
        </div>
      ) : (
        <div style={styles.emptyState}>
          <div style={styles.emptyTitle}>No quote created yet</div>
          <div style={styles.emptyCopy}>
            The quote draft will use workflow preparation parts, estimated labor, and current device SLA coverage.
          </div>
        </div>
      )}
    </div>
  );
};

const CaseWorkflowSaveBar: React.FC<{
  disabled: boolean;
  loading: boolean;
  saveLabel: string;
  helperText: string;
  onSave: () => void;
  onCancel: () => void;
  cancelLabel: string;
}> = ({ disabled, loading, saveLabel, helperText, onSave, onCancel, cancelLabel }) => (
  <div style={styles.caseSaveBar}>
    <div style={styles.caseSaveBarCopy}>
      <div style={styles.caseSaveBarTitle}>Save options</div>
      <div style={styles.caseSaveBarText}>{helperText}</div>
    </div>
    <div style={styles.primaryActions}>
      <button
        type="button"
        style={{ ...styles.button, ...styles.secondaryButton }}
        onClick={onCancel}
        disabled={disabled}
      >
        {cancelLabel}
      </button>
      <button
        type="button"
        style={{ ...styles.button, ...styles.primaryButton }}
        onClick={onSave}
        disabled={disabled}
      >
        {loading ? 'Saving...' : saveLabel}
      </button>
    </div>
  </div>
);

const Summary: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={styles.caseHeaderField}>
    <div style={styles.caseHeaderFieldLabel}>{label}</div>
    <div style={styles.caseHeaderFieldValue}>{value || 'Not set'}</div>
  </div>
);

const WorkflowPreviewModal: React.FC<{ viewer: any; onClose: () => void }> = ({ viewer, onClose }) => {
  const record = viewer?.record || {};
  const artifact = viewer?.artifact || {};
  const steps = Array.isArray(artifact.steps) ? artifact.steps : [];
  return (
    <div style={styles.previewBackdrop} onClick={event => event.stopPropagation()}>
      <div style={styles.previewModal} role="dialog" aria-modal="true" onClick={event => event.stopPropagation()}>
        <div style={styles.previewHeader}>
          <div>
            <div style={styles.previewTitle}>{record.title || artifact.title || 'Workflow'}</div>
            <div style={styles.sourceCardMeta}>
              {[record.deviceModelLabel || 'All models', record.version ? `v${record.version}` : '', `${steps.length} steps`]
                .filter(Boolean)
                .join(' | ')}
            </div>
          </div>
          <button type="button" style={{ ...styles.button, ...styles.primaryButton }} onClick={onClose}>
            Close
          </button>
        </div>
        <div style={styles.previewBody}>
          {artifact.description || record.description ? (
            <div style={styles.handoffText}>{artifact.description || record.description}</div>
          ) : null}
          <WorkflowProtocolPreview workflowJson={artifact} />
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    background: BRAND_SURFACE,
    minHeight: '100vh',
    padding: '2.5rem clamp(1rem, 4vw, 3rem) 4rem',
    color: BRAND_TEXT
  },
  shell: {
    maxWidth: '1600px',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.75rem',
    boxSizing: 'border-box'
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2.7rem',
    padding: '0.65rem 1rem',
    cursor: 'pointer',
    color: BRAND_TEXT,
    textDecoration: 'none',
    fontWeight: 700,
    backgroundColor: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
  },
  savedAt: {
    color: BRAND_MUTED,
    fontSize: '0.875rem'
  },
  caseHeaderCard: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 20px 40px -28px rgba(18, 63, 67, 0.22)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  caseHeaderTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap',
    alignItems: 'flex-start'
  },
  caseHeaderCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.55rem',
    minWidth: 0
  },
  caseHeroTitle: {
    margin: 0,
    fontSize: '1.75rem',
    fontWeight: 800,
    color: BRAND_TEXT,
    lineHeight: 1.15
  },
  caseHeroProblem: {
    fontWeight: 600,
    color: BRAND_TEXT,
    maxWidth: '58rem',
    lineHeight: 1.4
  },
  caseHeroActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  casePersistenceNote: {
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_ACCENT_BG,
    borderRadius: '0.85rem',
    padding: '0.85rem 1rem',
    color: BRAND_TEXT,
    lineHeight: 1.45,
    fontWeight: 600
  },
  caseHeaderGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))'
  },
  caseHeaderField: {
    minWidth: 0
  },
  caseHeaderFieldLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  caseHeaderFieldValue: {
    color: BRAND_TEXT,
    fontWeight: 700,
    overflowWrap: 'anywhere'
  },
  section: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 15px 35px -30px rgba(18, 63, 67, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  prepCard: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 15px 35px -30px rgba(18, 63, 67, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  statsCard: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 15px 35px -30px rgba(18, 63, 67, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  sectionTitle: {
    color: BRAND_TEXT,
    fontSize: '1.1rem',
    fontWeight: 800
  },
  sectionSub: {
    marginTop: '0.3rem',
    color: BRAND_MUTED,
    lineHeight: 1.4
  },
  statusChip: {
    borderRadius: '0.55rem',
    padding: '0.32rem 0.65rem',
    backgroundColor: BRAND_SUCCESS_BG,
    color: BRAND_SUCCESS_TEXT,
    fontWeight: 800,
    fontSize: '0.78rem'
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '9999px',
    padding: '0.45rem 1.1rem',
    fontSize: '0.85rem',
    fontWeight: 700,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT
  },
  statusHeader: {
    display: 'flex',
    gap: '1.75rem',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  statusLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    minWidth: '16rem',
    flex: '1 1 18rem'
  },
  statusRight: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    alignItems: 'flex-end',
    flex: '0 0 auto'
  },
  statusChipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  statusMetaRow: {
    display: 'flex',
    gap: '1.25rem',
    flexWrap: 'wrap'
  },
  statusMetaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    minWidth: '10rem',
    flex: '1 1 10rem'
  },
  fieldLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  fieldValue: {
    color: BRAND_TEXT,
    fontWeight: 700,
    overflowWrap: 'anywhere'
  },
  formGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))'
  },
  compactSourceGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))'
  },
  sourceModeGrid: {
    display: 'grid',
    gap: '0.8rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))'
  },
  sourceModeCard: {
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_CARD,
    borderRadius: '0.8rem',
    padding: '0.95rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    alignItems: 'flex-start',
    textAlign: 'left',
    color: BRAND_TEXT,
    cursor: 'pointer',
    minHeight: '5.25rem',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
  },
  sourceModeCardActive: {
    borderColor: BRAND_TEXT,
    background: BRAND_ACCENT_BG,
    boxShadow: '0 10px 20px -18px rgba(18, 63, 67, 0.45)'
  },
  sourceModeTitle: {
    fontWeight: 800,
    color: BRAND_TEXT,
    fontSize: '1rem'
  },
  sourceModeMeta: {
    color: BRAND_MUTED,
    lineHeight: 1.35,
    fontSize: '0.9rem'
  },
  sourceActionGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))'
  },
  sourceCard: {
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_ACCENT_BG,
    borderRadius: '0.8rem',
    padding: '0.9rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem',
    minWidth: 0
  },
  sourceCardTitle: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 800
  },
  sourceCardMain: {
    color: BRAND_TEXT,
    fontWeight: 800,
    overflowWrap: 'anywhere'
  },
  sourceCardMeta: {
    color: BRAND_MUTED,
    fontSize: '0.88rem',
    lineHeight: 1.4
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem',
    minWidth: 0
  },
  formLabel: {
    fontSize: '0.85rem',
    color: BRAND_TEXT,
    fontWeight: 800
  },
  input: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.75rem 0.85rem',
    width: '100%',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  select: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.75rem 0.85rem',
    width: '100%',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT,
    boxSizing: 'border-box'
  },
  textarea: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.8rem 0.9rem',
    width: '100%',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT,
    boxSizing: 'border-box',
    minHeight: '6.5rem',
    resize: 'vertical',
    fontFamily: 'inherit',
    lineHeight: 1.45
  },
  fieldset: {
    border: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1.75rem'
  },
  button: {
    borderRadius: '0.5rem',
    padding: '0.65rem 1.4rem',
    fontWeight: 800,
    cursor: 'pointer',
    minHeight: '2.7rem'
  },
  primaryButton: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    boxShadow: '0 10px 20px -16px rgba(18, 63, 67, 0.48)'
  },
  secondaryButton: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
  },
  statusActionComplete: {
    background: BRAND_SUCCESS_TEXT,
    border: `1px solid ${BRAND_SUCCESS_TEXT}`,
    color: '#ffffff',
    boxShadow: '0 10px 20px -16px rgba(37, 98, 77, 0.35)'
  },
  prepGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
    gap: '1rem'
  },
  workPlanPanel: {
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_PANEL,
    borderRadius: '0.9rem',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  caseSaveBar: {
    position: 'sticky',
    top: '0.75rem',
    zIndex: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_CARD,
    borderRadius: '0.9rem',
    padding: '0.9rem 1rem',
    boxShadow: '0 12px 28px -22px rgba(18, 63, 67, 0.35)'
  },
  caseSaveBarCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
    minWidth: 0,
    flex: '1 1 20rem'
  },
  caseSaveBarTitle: {
    color: BRAND_TEXT,
    fontWeight: 800,
    fontSize: '0.98rem'
  },
  caseSaveBarText: {
    color: BRAND_MUTED,
    lineHeight: 1.4,
    fontSize: '0.88rem'
  },
  workPlanStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
    gap: '0.8rem'
  },
  workPlanMetric: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    background: BRAND_CARD,
    padding: '0.85rem',
    textAlign: 'center'
  },
  workPlanMetricValue: {
    color: BRAND_TEXT,
    fontSize: '1.9rem',
    lineHeight: 1,
    fontWeight: 800
  },
  prepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem',
    minWidth: 0
  },
  prepItems: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.45rem'
  },
  prepChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '999px',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    padding: '0.32rem 0.65rem',
    fontWeight: 700,
    fontSize: '0.84rem'
  },
  noticeList: {
    display: 'grid',
    gap: '0.65rem'
  },
  noticeRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '0.75rem',
    alignItems: 'center',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    background: BRAND_PANEL,
    padding: '0.75rem'
  },
  noticeTitle: {
    color: BRAND_TEXT,
    fontWeight: 800,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  primaryActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  actionRow: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  divider: {
    height: 1,
    backgroundColor: BRAND_BORDER
  },
  contextStrip: {
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_ACCENT_BG,
    borderRadius: '0.8rem',
    padding: '0.8rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    color: BRAND_TEXT,
    lineHeight: 1.4
  },
  handoffBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  handoffTitle: {
    color: BRAND_TEXT,
    fontWeight: 800,
    fontSize: '1.05rem'
  },
  handoffText: {
    marginTop: '0.35rem',
    color: BRAND_MUTED,
    lineHeight: 1.45
  },
  runNotes: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  stepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  stepCard: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    backgroundColor: BRAND_PANEL,
    padding: '0.95rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem'
  },
  stepHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  stepCompleteToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    color: BRAND_TEXT,
    fontWeight: 800,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_CARD,
    borderRadius: '999px',
    padding: '0.42rem 0.75rem'
  },
  stepTitle: {
    color: BRAND_TEXT,
    fontWeight: 800,
    lineHeight: 1.3
  },
  stepInstruction: {
    color: BRAND_TEXT,
    lineHeight: 1.45
  },
  stepWarning: {
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.7rem',
    background: BRAND_CARD,
    color: BRAND_TEXT,
    padding: '0.65rem 0.75rem',
    lineHeight: 1.4
  },
  stepMeta: {
    color: BRAND_MUTED,
    fontSize: '0.86rem',
    lineHeight: 1.4
  },
  checkList: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    background: BRAND_CARD,
    display: 'grid',
    overflow: 'hidden'
  },
  checkRow: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gap: '0.75rem',
    alignItems: 'center',
    padding: '0.65rem 0.75rem',
    borderBottom: `1px solid ${BRAND_BORDER}`
  },
  checkText: {
    color: BRAND_TEXT,
    lineHeight: 1.4
  },
  checkSpec: {
    color: BRAND_MUTED,
    fontWeight: 500
  },
  captureInput: {
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.55rem 0.7rem',
    width: '100%',
    fontSize: '0.92rem',
    background: BRAND_PANEL,
    color: BRAND_TEXT,
    boxSizing: 'border-box',
    gridColumn: '2 / -1',
    maxWidth: '18rem'
  },
  previewCheckRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    padding: '0.65rem 0.75rem',
    borderBottom: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT
  },
  captureBadge: {
    borderRadius: '999px',
    background: BRAND_SUCCESS_BG,
    color: BRAND_SUCCESS_TEXT,
    padding: '0.25rem 0.55rem',
    fontWeight: 800,
    fontSize: '0.75rem',
    whiteSpace: 'nowrap'
  },
  emptyState: {
    border: `1px dashed ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    background: BRAND_PANEL,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
    color: BRAND_MUTED
  },
  emptyTitle: {
    color: BRAND_TEXT,
    fontWeight: 800
  },
  emptyCopy: {
    color: BRAND_MUTED,
    lineHeight: 1.45
  },
  muted: {
    color: BRAND_MUTED,
    fontSize: '0.95rem'
  },
  readOnlyBanner: {
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    borderRadius: '1rem',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    padding: '0.9rem 1.2rem',
    fontWeight: 700
  },
  error: {
    border: `1px solid ${BRAND_DANGER_TEXT}`,
    borderRadius: '1rem',
    padding: '0.9rem 1.2rem',
    color: BRAND_DANGER_TEXT,
    backgroundColor: BRAND_DANGER_BG,
    fontWeight: 700
  },
  inlineError: {
    border: `1px solid ${BRAND_DANGER_TEXT}`,
    borderRadius: '0.8rem',
    padding: '0.75rem 0.9rem',
    color: BRAND_DANGER_TEXT,
    backgroundColor: BRAND_DANGER_BG,
    fontWeight: 700
  },
  successChip: {
    background: BRAND_SUCCESS_BG,
    color: BRAND_SUCCESS_TEXT
  },
  warningChip: {
    background: BRAND_WARNING_BG,
    color: BRAND_WARNING_TEXT
  },
  ghostButton: {
    background: 'transparent',
    borderColor: BRAND_BORDER,
    color: BRAND_TEXT,
    minHeight: '2.35rem',
    padding: '0.55rem 0.8rem'
  },
  compactButton: {
    minHeight: '2.35rem',
    minWidth: '6.75rem',
    padding: '0.45rem 0.75rem'
  },
  quotePanel: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    background: BRAND_PANEL,
    padding: '1.1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  quoteStatusRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  savedQuoteRow: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    borderRadius: '0.75rem',
    padding: '0.75rem 0.85rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  savedQuoteMain: {
    minWidth: 0,
    flex: '1 1 24rem'
  },
  savedQuoteTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  savedQuoteTitle: {
    color: BRAND_TEXT,
    fontWeight: 800,
    overflowWrap: 'anywhere'
  },
  savedQuoteMeta: {
    color: BRAND_MUTED,
    fontSize: '0.82rem',
    marginTop: '0.2rem'
  },
  savedQuoteDecision: {
    color: BRAND_ACCENT_TEXT,
    fontSize: '0.78rem',
    marginTop: '0.25rem'
  },
  savedQuoteActions: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap'
  },
  quoteSummaryGrid: {
    display: 'grid',
    gap: '0.8rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))'
  },
  quoteSummaryCard: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.9rem',
    background: BRAND_CARD,
    padding: '0.95rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  quoteSummaryValue: {
    color: BRAND_TEXT,
    fontSize: '1.2rem',
    fontWeight: 800
  },
  quoteNotice: {
    border: `1px solid ${BRAND_WARNING_BORDER}`,
    borderRadius: '0.85rem',
    background: BRAND_WARNING_BG,
    color: BRAND_WARNING_TEXT,
    padding: '0.8rem 0.9rem',
    fontWeight: 600,
    lineHeight: 1.4
  },
  quoteMetaGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))'
  },
  quoteToolbar: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  quoteTable: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.9rem',
    background: BRAND_CARD,
    overflow: 'hidden',
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  quoteTableRow: {
    display: 'grid',
    gridTemplateColumns: '6rem minmax(18rem, 2.2fr) 6rem 8rem 10rem 8rem 6rem',
    gap: '0.75rem',
    padding: '0.8rem 0.9rem',
    alignItems: 'start',
    borderTop: `1px solid ${BRAND_BORDER}`
  },
  quoteTableHeader: {
    borderTop: 'none',
    background: BRAND_PANEL,
    color: BRAND_MUTED,
    fontSize: '0.78rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  quoteTypeCell: {
    color: BRAND_TEXT,
    fontWeight: 700,
    paddingTop: '0.55rem'
  },
  quoteLineMeta: {
    color: BRAND_MUTED,
    fontSize: '0.78rem',
    marginTop: '0.3rem'
  },
  quoteSlaBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '2rem',
    padding: '0.25rem 0.55rem',
    borderRadius: '999px',
    background: BRAND_SUCCESS_BG,
    color: BRAND_SUCCESS_TEXT,
    fontWeight: 700,
    fontSize: '0.78rem',
    lineHeight: 1.2
  },
  quoteTotalCell: {
    color: BRAND_TEXT,
    fontWeight: 800,
    paddingTop: '0.55rem'
  },
  quoteTotalsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.9rem',
    background: BRAND_CARD,
    padding: '0.9rem 1rem'
  },
  quoteTotalsText: {
    color: BRAND_MUTED,
    fontWeight: 600
  },
  quoteGrandTotal: {
    color: BRAND_TEXT,
    fontSize: '1.2rem',
    fontWeight: 800
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    overflowY: 'auto',
    padding: '3rem 1rem',
    zIndex: 2600
  },
  modalContent: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    padding: '1.75rem',
    maxWidth: '720px',
    width: '100%',
    boxSizing: 'border-box',
    boxShadow: '0 24px 40px rgba(18, 63, 67, 0.18)'
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  modalFooter: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.75rem',
    marginTop: '1.5rem'
  },
  modalHeader: {
    marginBottom: '1rem'
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: BRAND_TEXT,
    marginBottom: '0.5rem'
  },
  inlineNotice: {
    background: BRAND_ACCENT_BG,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_ACCENT_BG}`,
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    marginBottom: '1rem'
  },
  dangerButton: {
    background: BRAND_DANGER_TEXT,
    borderColor: BRAND_DANGER_TEXT,
    color: '#fff'
  },
  warningButton: {
    background: BRAND_WARNING_BG,
    borderColor: BRAND_WARNING_BORDER,
    color: BRAND_WARNING_TEXT
  },
  previewBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    zIndex: 2600
  },
  previewModal: {
    width: 'min(980px, 96vw)',
    maxHeight: '92vh',
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    boxShadow: '0 28px 90px -36px rgba(15, 23, 42, 0.55)',
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    overflow: 'hidden'
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '1rem 1.15rem',
    borderBottom: `1px solid ${BRAND_BORDER}`
  },
  previewTitle: {
    color: BRAND_TEXT,
    fontSize: '1.25rem',
    fontWeight: 800,
    lineHeight: 1.2
  },
  previewBody: {
    minHeight: 0,
    overflow: 'auto',
    padding: '1rem',
    display: 'grid',
    gap: '1rem',
    background: BRAND_PANEL
  }
};

export default SafetyUpgradeTask;
